import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';
import { saveAs } from 'file-saver';
import './FullStackCoursePage.css';
import courseHighlightsData from '../../data/fullstack/courseHighlights.json';
import syllabusData from '../../data/fullstack/syllabus.json';

const techMasteryList = [
  { name: 'HTML5', color: '#e34f26', glowColor: 'rgba(227, 79, 38, 0.3)' },
  { name: 'CSS3', color: '#1572b6', glowColor: 'rgba(21, 114, 182, 0.3)' },
  { name: 'JavaScript', color: '#f7df1e', textColor: '#857100', glowColor: 'rgba(247, 223, 30, 0.4)' },
  { name: 'React', color: '#0083a0', glowColor: 'rgba(0, 131, 160, 0.35)' },
  { name: 'Java', color: '#5382a1', glowColor: 'rgba(83, 130, 161, 0.35)' },
  { name: 'Spring', color: '#6db33f', glowColor: 'rgba(109, 179, 63, 0.35)' },
  { name: 'Spring Boot', color: '#4c8427', glowColor: 'rgba(76, 132, 39, 0.35)' },
  { name: 'MySQL', color: '#b56800', glowColor: 'rgba(181, 104, 0, 0.35)' },
  { name: 'Deployment', color: '#0284c7', glowColor: 'rgba(2, 132, 199, 0.35)' }
];

function FullStackCoursePage() {
  const navigate = useNavigate();
  const [activeTrack, setActiveTrack] = useState('frontend'); // 'frontend' (default open) or 'backend'
  const [frontendSubCategory, setFrontendSubCategory] = useState('all'); // 'all', 'html', 'css', 'javascript', 'react'
  const [backendSubCategory, setBackendSubCategory] = useState('all'); // 'all', 'java', 'sql', 'jdbc', 'jsp', 'git', etc.
  const [selectedTopicIndex, setSelectedTopicIndex] = useState(0);
  const [projectFilter, setProjectFilter] = useState('all'); // 'all', 'major', 'frontend', 'backend'
  const [activeTechIndex, setActiveTechIndex] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const itemsListRef = useRef(null);

  // Auto scroll sub-topics list to top when module, track, or category changes
  useEffect(() => {
    if (itemsListRef.current) {
      itemsListRef.current.scrollTop = 0;
    }
  }, [selectedTopicIndex, activeTrack, frontendSubCategory, backendSubCategory]);



  // Project Details Modal State
  const [selectedProjectModal, setSelectedProjectModal] = useState(null);
  const [activeModalImageIndex, setActiveModalImageIndex] = useState(0);
  const [showMoreScreens, setShowMoreScreens] = useState(false);

  // Flagship Carousel State & Auto-play Timer
  const [flagshipIndex, setFlagshipIndex] = useState(0);
  const [isPlayingFlagshipVideo, setIsPlayingFlagshipVideo] = useState(false);
  const [isHoveringFlagship, setIsHoveringFlagship] = useState(false);

  const flagshipCardsData = useMemo(() => [
    {
      id: 'subscription',
      tag: 'BACKED BY CORE2WEB 2026',
      title: 'The Subscription',
      urlPill: 'thesubscription.in',
      url: 'https://thesubscription.in/',
      desc: 'Shashi Sir and Core2web actively back and mentor students to build real, revenue-generating tech startups and production SaaS platforms. <strong>The Subscription</strong> is an enterprise SaaS platform engineered by Core2web developers and incubated under <strong>Core2web Systems</strong>.',
      btnText: 'Visit live website at thesubscription.in',
      image: '/assets/full%20stack/sir-ranjeet.jpeg',
      videoUrl: 'https://www.youtube.com/embed/JS4w0fFArb8?autoplay=1&rel=0',
      founderName: 'Ranjeet Bhosale',
      founderRole: 'Founder',
      cofounderName: 'Shashi Sir',
      cofounderRole: 'Co-founder',
      caption: 'Backed by Core2web'
    },
    {
      id: 'playz',
      tag: 'BACKED BY CORE2WEB 2026',
      title: 'PlayZ',
      desc: '<strong>PlayZ</strong> is a turf booking application designed to make sports venue discovery and booking simple, fast, and convenient. Built by students as part of the startup incubation ecosystem at <strong>Core2web</strong>, PlayZ focuses on solving real-world problems through technology.<br /><br />The platform enables users to discover available turfs, check booking options, and reserve sports venues with ease—while giving the student team hands-on experience in building and developing a real-world product.',
      image: '/assets/full%20stack/playZ-team.png',
      videoUrl: 'https://www.youtube.com/embed/i_B2pSlKH8U?autoplay=1&rel=0',
      founderName: 'Core2web Squad',
      founderRole: 'Engineers',
      cofounderName: 'Shashi Sir',
      cofounderRole: 'Mentor & Co-founder',
      caption: 'Incubated at Core2web'
    }
  ], []);

  // Touch / Swipe support for Flagship Carousel
  const [flagshipTouchStart, setFlagshipTouchStart] = useState(null);

  const handleFlagshipTouchStart = (e) => {
    if (e.touches && e.touches[0]) {
      setFlagshipTouchStart(e.touches[0].clientX);
    }
  };

  const handleFlagshipTouchEnd = (e) => {
    if (flagshipTouchStart === null) return;
    if (e.changedTouches && e.changedTouches[0]) {
      const diff = flagshipTouchStart - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) {
        if (diff > 0) {
          handleNextFlagship();
        } else {
          handlePrevFlagship();
        }
      }
    }
    setFlagshipTouchStart(null);
  };

  useEffect(() => {
    if (flagshipCardsData.length <= 1) return;
    if (isPlayingFlagshipVideo || isHoveringFlagship) return;
    const timer = setInterval(() => {
      setFlagshipIndex((prev) => (prev + 1) % flagshipCardsData.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [flagshipCardsData.length, isPlayingFlagshipVideo, isHoveringFlagship]);

  const handleNextFlagship = () => {
    if (flagshipCardsData.length <= 1) return;
    setIsPlayingFlagshipVideo(false);
    setFlagshipIndex((prev) => (prev + 1) % flagshipCardsData.length);
  };

  const handlePrevFlagship = () => {
    if (flagshipCardsData.length <= 1) return;
    setIsPlayingFlagshipVideo(false);
    setFlagshipIndex((prev) => (prev - 1 + flagshipCardsData.length) % flagshipCardsData.length);
  };

  // Mentorship & Guidance 10 Key Highlights Data
  const mentorshipHighlights = useMemo(() => [
    {
      emoji: '👨‍🏫',
      title: 'Personal Mentorship',
      desc: 'Learn directly from experienced industry mentors.',
      color: '#00288e',
      bgColor: '#e0e8ff'
    },
    {
      emoji: '💻',
      title: 'Real-World Project Guidance',
      desc: 'Build practical projects with proper industry workflows.',
      color: '#0083a0',
      bgColor: '#e0f2fe'
    },
    {
      emoji: '🧠',
      title: 'Logic & Problem-Solving Sessions',
      desc: 'Strengthen programming logic and analytical thinking.',
      color: '#712ae2',
      bgColor: '#f3e8ff'
    },
    {
      emoji: '🏗️',
      title: 'Project Architecture Guidance',
      desc: 'Understand how to structure and develop scalable applications.',
      color: '#4c8427',
      bgColor: '#dcfce7'
    },
    {
      emoji: '🔍',
      title: 'Code Reviews',
      desc: 'Get feedback on coding practices, quality, and improvements.',
      color: '#d97706',
      bgColor: '#fef3c7'
    },
    {
      emoji: '🧑‍💼',
      title: 'Career & Placement Guidance',
      desc: 'Resume, interview, and job-preparation support.',
      color: '#2563eb',
      bgColor: '#dbeafe'
    },
    {
      emoji: '🎯',
      title: 'Personalized Learning Roadmap',
      desc: 'Clear direction on what to learn and what to build.',
      color: '#dc2626',
      bgColor: '#fee2e2'
    },
    {
      emoji: '🤝',
      title: 'Team & Collaboration Skills',
      desc: 'Learn Git, teamwork, task management, and professional communication.',
      color: '#0891b2',
      bgColor: '#cff4fc'
    },
    {
      emoji: '🚀',
      title: 'Industry-Oriented Development',
      desc: 'Follow practices used in professional software development.',
      color: '#9333ea',
      bgColor: '#f3e8ff'
    },
    {
      emoji: '📈',
      title: 'Continuous Progress Tracking',
      desc: 'Regular feedback to identify strengths and improvement areas.',
      color: '#16a34a',
      bgColor: '#dcfce7'
    }
  ], []);

  // Aptitude Preparation Section State & Data
  const [aptitudeCategory, setAptitudeCategory] = useState('quant');
  const [aptitudeActiveTopicIndex, setAptitudeActiveTopicIndex] = useState(0);

  const aptitudeCategories = useMemo(() => ({
    quant: {
      title: 'Quantitative Aptitude',
      icon: 'bar_chart',
      count: '14 Topics',
      topics: [
        { name: 'Time & Work', questions: '35+ Questions', duration: '3.5 Hrs Video' },
        { name: 'Ratio & Proportion', questions: '25+ Questions', duration: '2.5 Hrs Video' },
        { name: 'Profit & Loss', questions: '30+ Questions', duration: '3.0 Hrs Video' },
        { name: 'Permutations & Combinations', questions: '28+ Questions', duration: '2.8 Hrs Video' },
        { name: 'Probability', questions: '24+ Questions', duration: '2.4 Hrs Video' },
        { name: 'Simple & Compound Interest', questions: '22+ Questions', duration: '2.2 Hrs Video' },
        { name: 'Time, Speed & Distance', questions: '32+ Questions', duration: '3.2 Hrs Video' },
        { name: 'Averages & Alligations', questions: '20+ Questions', duration: '2.0 Hrs Video' },
        { name: 'Number Systems & Divisibility', questions: '30+ Questions', duration: '3.0 Hrs Video' },
        { name: 'Pipes & Cisterns', questions: '18+ Questions', duration: '1.8 Hrs Video' },
        { name: 'Percentages & Applications', questions: '25+ Questions', duration: '2.5 Hrs Video' },
        { name: 'Boats & Streams', questions: '16+ Questions', duration: '1.5 Hrs Video' },
        { name: 'Mixtures & Allegations', questions: '20+ Questions', duration: '2.0 Hrs Video' },
        { name: 'Mensuration & Geometry', questions: '25+ Questions', duration: '2.5 Hrs Video' }
      ]
    },
    logical: {
      title: 'Logical Reasoning',
      icon: 'psychology',
      count: '11 Topics',
      topics: [
        { name: 'Blood Relations', questions: '24+ Questions', duration: '2.2 Hrs Video' },
        { name: 'Coding-Decoding', questions: '26+ Questions', duration: '2.4 Hrs Video' },
        { name: 'Seating Arrangements (Linear & Circular)', questions: '35+ Questions', duration: '3.5 Hrs Video' },
        { name: 'Number & Letter Series', questions: '28+ Questions', duration: '2.6 Hrs Video' },
        { name: 'Syllogisms & Logical Deductions', questions: '30+ Questions', duration: '3.0 Hrs Video' },
        { name: 'Direction Sense Test', questions: '20+ Questions', duration: '1.8 Hrs Video' },
        { name: 'Clocks & Calendars', questions: '22+ Questions', duration: '2.0 Hrs Video' },
        { name: 'Puzzles & Analytical Reasoning', questions: '32+ Questions', duration: '3.2 Hrs Video' },
        { name: 'Data Sufficiency', questions: '20+ Questions', duration: '2.0 Hrs Video' },
        { name: 'Order & Ranking', questions: '18+ Questions', duration: '1.6 Hrs Video' },
        { name: 'Statement & Assumptions / Conclusions', questions: '22+ Questions', duration: '2.0 Hrs Video' }
      ]
    },
    verbal: {
      title: 'Verbal Ability',
      icon: 'spellcheck',
      count: '9 Topics',
      topics: [
        { name: 'Sentence Correction & Spotting Errors', questions: '30+ Questions', duration: '2.8 Hrs Video' },
        { name: 'Reading Comprehension & Passages', questions: '25+ Questions', duration: '2.5 Hrs Video' },
        { name: 'Synonyms, Antonyms & Word Power', questions: '35+ Questions', duration: '3.0 Hrs Video' },
        { name: 'Common Grammar Rules & Applications', questions: '28+ Questions', duration: '2.6 Hrs Video' },
        { name: 'Idioms, Phrases & One Word Substitution', questions: '25+ Questions', duration: '2.2 Hrs Video' },
        { name: 'Para Jumbles & Sentence Rearrangement', questions: '22+ Questions', duration: '2.0 Hrs Video' },
        { name: 'Cloze Test & Fillers', questions: '20+ Questions', duration: '1.8 Hrs Video' },
        { name: 'Critical Reasoning & Analogies', questions: '20+ Questions', duration: '1.8 Hrs Video' },
        { name: 'Active & Passive Voice / Direct & Indirect', questions: '18+ Questions', duration: '1.6 Hrs Video' }
      ]
    }
  }), []);

  const currentAptCategory = aptitudeCategories[aptitudeCategory];
  const activeAptTopic = currentAptCategory.topics[aptitudeActiveTopicIndex] || currentAptCategory.topics[0];

  const handleAptCategoryChange = (key) => {
    setAptitudeCategory(key);
    setAptitudeActiveTopicIndex(0);
  };

  const openProjectModal = (project) => {
    setSelectedProjectModal(project);
    setActiveModalImageIndex(0);
    setShowMoreScreens(false);
  };
  const closeProjectModal = () => {
    setSelectedProjectModal(null);
    setShowMoreScreens(false);
  };

  // Enrollment Modal & Form State
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enrollForm, setEnrollForm] = useState({
    name: '',
    contact: '',
    college: '',
    year: 'FE',
    seatType: 'admission', // 'admission' (Confirm seat for admission) or 'demo' (Confirm seat for demo lecture)
    gender: 'Male',
    reference: '',
  });

  const handleEnrollChange = (field, value) => {
    if (field === 'contact') {
      // Keep digits only, max 10 chars
      const numeric = value.replace(/\D/g, '').slice(0, 10);
      setEnrollForm((prev) => ({ ...prev, contact: numeric }));
      return;
    }
    setEnrollForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    if (!enrollForm.name.trim()) {
      alert('Please enter your Full Name.');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(enrollForm.contact)) {
      alert('Please enter a valid 10-digit Indian Mobile Number starting with 6, 7, 8, or 9.');
      return;
    }
    if (!enrollForm.college.trim()) {
      alert('Please enter your College Name.');
      return;
    }

    setIsSubmitting(true);

    const GOOGLE_SCRIPT_URL =
      'https://script.google.com/macros/s/AKfycbw2yCvqlOBd0z_MJZkwBlrR65bN66DSdrIdcQoGHCPuV3ebBM2RujKownZdUEMttVly/exec';

    const now = new Date();
    const formattedDate =
      now.toLocaleDateString('en-GB') +
      ' ' +
      now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    const seatLabel =
      enrollForm.seatType === 'admission'
        ? 'Confirm seat for admission'
        : 'Confirm seat for demo lecture';

    const payload = {
      date: formattedDate,
      name: enrollForm.name.trim(),
      contact: enrollForm.contact.trim(),
      college: enrollForm.college.trim(),
      year: enrollForm.year,
      gender: enrollForm.gender,
      ref: enrollForm.reference.trim() || 'Direct',
      seatType: seatLabel,
      Date: formattedDate,
      Name: enrollForm.name.trim(),
      Contact: enrollForm.contact.trim(),
      College: enrollForm.college.trim(),
      Year: enrollForm.year,
      Gender: enrollForm.gender,
      Ref: enrollForm.reference.trim() || 'Direct',
      SeatType: seatLabel,
    };

    const queryParams = new URLSearchParams({
      date: formattedDate,
      name: enrollForm.name.trim(),
      contact: enrollForm.contact.trim(),
      college: enrollForm.college.trim(),
      year: enrollForm.year,
      gender: enrollForm.gender,
      ref: enrollForm.reference.trim() || 'Direct',
      seatType: seatLabel,
      Date: formattedDate,
      Name: enrollForm.name.trim(),
      Contact: enrollForm.contact.trim(),
      College: enrollForm.college.trim(),
      Year: enrollForm.year,
      Gender: enrollForm.gender,
      Ref: enrollForm.reference.trim() || 'Direct',
      SeatType: seatLabel,
    }).toString();

    try {
      await fetch(`${GOOGLE_SCRIPT_URL}?${queryParams}`, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify(payload),
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form to Google Sheet:', error);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEnrollModal = (seatPreference = 'admission') => {
    setEnrollForm((prev) => ({ ...prev, seatType: seatPreference }));
    setIsSubmitted(false);
    setIsEnrollModalOpen(true);
  };

  const closeEnrollModal = () => {
    setIsEnrollModalOpen(false);
    setIsSubmitted(false);
  };

  // Syllabus Download Enquiry Modal State
  const [isSyllabusModalOpen, setIsSyllabusModalOpen] = useState(false);
  const [isSyllabusSubmitted, setIsSyllabusSubmitted] = useState(false);
  const [isSyllabusSubmitting, setIsSyllabusSubmitting] = useState(false);
  const [syllabusForm, setSyllabusForm] = useState({
    userName: '',
    userNumber: '',
    reference: '',
  });

  const handleSyllabusChange = (field, value) => {
    if (field === 'userNumber') {
      const numeric = value.replace(/\D/g, '').slice(0, 10);
      setSyllabusForm((prev) => ({ ...prev, userNumber: numeric }));
      return;
    }
    setSyllabusForm((prev) => ({ ...prev, [field]: value }));
  };

  const openSyllabusModal = () => {
    setIsSyllabusModalOpen(true);
    setIsSyllabusSubmitted(false);
    setIsSyllabusSubmitting(false);
  };

  const closeSyllabusModal = () => {
    setIsSyllabusModalOpen(false);
    setIsSyllabusSubmitted(false);
    setIsSyllabusSubmitting(false);
  };

  const triggerPDFDownload = () => {
    const pdfUrl = '/assets/pdf/full_stack_syllabus.pdf';
    const fileName = 'Java_Fullstack_Complete_Syllabus.pdf';

    try {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
      }, 500);
    } catch (err) {
      console.warn('PDF download link error:', err);
      window.open(pdfUrl, '_blank');
    }
  };

  const handleSyllabusSubmit = async (e) => {
    e.preventDefault();
    if (!syllabusForm.userName.trim()) {
      alert('Please enter your Name.');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(syllabusForm.userNumber)) {
      alert('Please enter a valid 10-digit Contact number starting with 6, 7, 8, or 9.');
      return;
    }

    setIsSyllabusSubmitting(true);

    // 1. Immediately trigger the download in 0ms so browser never blocks it
    triggerPDFDownload();

    const formatNow = () => {
      try {
        const date = new Date();
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDateSplit = date.toLocaleDateString('en-GB', options).replace(/\//g, '-').split('-');
        const formattedDate = `${formattedDateSplit[2]}-${formattedDateSplit[1]}-${formattedDateSplit[0]}`;
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes}:00`;
        return `${formattedDate} ${formattedTime}`;
      } catch (err) {
        return new Date().toISOString();
      }
    };

    const now = new Date();
    const formattedDate =
      now.toLocaleDateString('en-GB') +
      ' ' +
      now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    // Save data to Enquiry / Contact Us Sheet Google Apps Script URL
    const ENQUIRY_SCRIPT_URL =
      'https://script.google.com/macros/s/AKfycbzOh_Tj3DBaagd2iYN1CQ0mREBM8n3fLEtjvYfAFXr2yji5FpLBm9AL4A7b1BKYbygt_Q/exec';

    const sheetPayload = {
      date: formatNow(),
      name: syllabusForm.userName.trim(),
      number: syllabusForm.userNumber.trim(),
      contact: syllabusForm.userNumber.trim(),
      email: '',
      college: '',
      branch: '',
      year: '',
      batches: '',
      courseIntrested: '',
      reference: syllabusForm.reference.trim() || '',
      ref: syllabusForm.reference.trim() || '',
      message: '',
      type: '',
      'Call Remark': '',
      'followup date': '',
      reason: ''
    };

    const formData = new FormData();
    Object.keys(sheetPayload).forEach((key) => {
      formData.append(key, sheetPayload[key]);
    });

    const queryParams = new URLSearchParams({
      ...sheetPayload,
      Date: formattedDate,
      Name: syllabusForm.userName.trim(),
      Contact: syllabusForm.userNumber.trim(),
      Ref: syllabusForm.reference.trim() || '',
      SeatType: 'Syllabus Download',
      seatType: 'Syllabus Download'
    }).toString();

    // 2. Dispatch all sheet synchronization requests in parallel with timeout protection
    try {
      const fetchWithTimeout = (url, options = {}, timeoutMs = 3500) => {
        return Promise.race([
          fetch(url, options),
          new Promise((resolve) => setTimeout(() => resolve({ ok: true }), timeoutMs))
        ]);
      };

      await Promise.allSettled([
        fetchWithTimeout(ENQUIRY_SCRIPT_URL, {
          method: 'POST',
          body: formData,
          mode: 'no-cors'
        }),
        fetchWithTimeout(`https://script.google.com/macros/s/AKfycbw2yCvqlOBd0z_MJZkwBlrR65bN66DSdrIdcQoGHCPuV3ebBM2RujKownZdUEMttVly/exec?${queryParams}`, {
          method: 'POST',
          mode: 'no-cors'
        }),
        fetchWithTimeout(`https://script.google.com/macros/s/AKfycbywDQdhdATkKjbwwQ-napE0e8MGq2Ex411sF1BxxmSq7CJ3VXmHU-z60FttMr5AAXLS/exec?${queryParams}`, {
          method: 'POST',
          mode: 'no-cors'
        })
      ]);
    } catch (err) {
      console.warn('Sheet submission error:', err);
    } finally {
      // 3. Guaranteed stop loader and show success state immediately
      setIsSyllabusSubmitting(false);
      setIsSyllabusSubmitted(true);
    }
  };

  // Infinite sequential border change loop (one by one)
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTechIndex((prev) => (prev + 1) % techMasteryList.length);
    }, 1100);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const origHtml = document.documentElement.style.height;
    const origBody = document.body.style.height;
    document.documentElement.style.height = 'auto';
    document.documentElement.style.overflowY = 'visible';
    document.body.style.height = 'auto';
    document.body.style.overflowY = 'visible';

    return () => {
      document.documentElement.style.height = origHtml;
      document.documentElement.style.overflowY = 'auto';
      document.body.style.height = origBody;
      document.body.style.overflowY = 'auto';
    };
  }, []);

  // Reset selected module index when switching tracks
  const handleTrackChange = (track) => {
    setActiveTrack(track);
    setFrontendSubCategory('all');
    setBackendSubCategory('all');
    setSelectedTopicIndex(0);
  };

  const handleFrontendCategoryChange = (category) => {
    setFrontendSubCategory(category);
    setSelectedTopicIndex(0);
  };

  const handleBackendCategoryChange = (category) => {
    setBackendSubCategory(category);
    setSelectedTopicIndex(0);
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const highlights = courseHighlightsData?.courseHighlights?.highlights || [];
  const tracks = syllabusData?.course?.tracks || {};

  // Extract Frontend modules
  const frontendModules = useMemo(() => {
    const list = [];
    if (tracks.frontend?.phases) {
      tracks.frontend.phases.forEach((phase) => {
        phase.modules?.forEach((mod) => {
          list.push({
            track: 'Frontend',
            category: phase.category || 'HTML',
            phase: phase.title,
            moduleNum: mod.module,
            title: mod.title,
            topics: mod.topics || [],
          });
        });
      });
    } else if (tracks.frontend?.modules) {
      tracks.frontend.modules.forEach((mod) => {
        list.push({
          track: 'Frontend',
          category: mod.category || 'HTML',
          moduleNum: mod.module,
          title: mod.title,
          topics: mod.topics || [],
        });
      });
    }
    return list;
  }, [tracks]);

  // Extract Backend modules (supports direct `modules` array or nested `phases`)
  const backendModules = useMemo(() => {
    const list = [];
    if (tracks.backend?.modules) {
      tracks.backend.modules.forEach((mod) => {
        list.push({
          track: 'Backend',
          category: mod.category || 'java',
          moduleNum: mod.module,
          title: mod.title,
          topics: mod.topics || [],
        });
      });
    } else if (tracks.backend?.phases) {
      tracks.backend.phases.forEach((phase) => {
        phase.modules?.forEach((mod) => {
          list.push({
            track: 'Backend',
            category: mod.category || phase.category || 'java',
            phase: phase.title,
            moduleNum: mod.module,
            title: mod.title,
            topics: mod.topics || [],
          });
        });
      });
    }
    return list;
  }, [tracks]);

  // Complete List of All 20 Real World Projects with UI details & topics covered
  const allProjectsList = useMemo(() => [
    // 10 Frontend Projects
    {
      id: 'proj-1',
      title: 'Personal Developer Portfolio',
      category: 'frontend',
      tags: ['HTML', 'CSS'],
      image: '/assets/full%20stack/portfolio-screen-1.png',
      moreCount: 1,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/portfolio-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/portfolio-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/portfolio-screen-3.png' },
        { label: 'Screen 4', url: '/assets/full%20stack/portfolio-screen-4.png' },
      ],
      desc: 'Sleek, responsive personal portfolio showcasing developer projects, interactive skills showcase, dark mode UI, and contact form.',
      topics: ['Semantic HTML5 Elements', 'CSS Grid & Flexbox Layouts', 'CSS Variables & Themes', 'Responsive Media Queries', 'Keyframe Animations'],
      features: ['Hero section with animated skills badge', 'Filterable projects showcase grid', 'Responsive mobile drawer navigation', 'Interactive contact form UI']
    },
    {
      id: 'proj-2',
      title: 'Shop.co - E-Commerce Shopping Platform',
      category: 'frontend',
      tags: ['HTML', 'CSS'],
      image: '/assets/full%20stack/Shop.co-screen-1.png',
      moreCount: 3,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/Shop.co-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/Shop.co-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/Shop.co-screen-3.png' },
        { label: 'Screen 4', url: '/assets/full%20stack/Shop.co-screen-4.png' },
      ],
      desc: 'Modern e-commerce fashion shopping platform featuring full responsive UI with home hero showcase, category filters, product details with reviews, and interactive shopping cart checkout.',
      topics: ['Multi-page HTML Architecture', 'E-Commerce Grid Layouts', 'Interactive Category Filters', 'Product Card Components', 'Shopping Cart State & Checkout UI'],
      features: ['Dynamic product grid with rating stars', 'Color & size variant selector UI', 'Interactive shopping cart & summary order total', 'Customer review slider & filter layout']
    },
    {
      id: 'proj-3',
      title: 'Bite.c2w - Food Ordering & Delivery App',
      category: 'frontend',
      tags: ['HTML', 'CSS'],
      image: '/assets/full%20stack/bite.c2w-screen-1.png',
      moreCount: 3,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/bite.c2w-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/bite.c2w-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/bite.c2w-screen-3.png' },
        { label: 'Screen 4', url: '/assets/full%20stack/bite.c2w-screen-4.png' },
        { label: 'Screen 5', url: '/assets/full%20stack/bite.c2w-screen-5.png' },
      ],
      desc: 'Complete full-stack food delivery platform featuring live restaurant listings, interactive food menus, cart management, and order tracking.',
      topics: ['Modern UI Layout Design', 'CSS Positioning & Flexbox', 'Food Menu Categorization', 'Cart Counter Badge UI', 'Card Hover Micro-interactions'],
      features: ['Live restaurant search interface', 'Interactive dish catalog with price tags', 'Cart summary drawer UI', 'Live order tracking timeline component']
    },
    {
      id: 'proj-4',
      title: 'EventPass - Event Ticketing & Pass Booking App',
      category: 'frontend',
      tags: ['HTML', 'CSS', 'JS', 'React'],
      image: '/assets/full%20stack/Even%20pass-screen-1.png',
      moreCount: 2,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/Even%20pass-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/Even%20pass-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/Even%20pass-screen-3.png' },
      ],
      desc: 'Modern event booking platform for discovering upcoming tech summits, concerts, workshops, selecting pass tiers, generating QR code tickets, and managing digital event passes.',
      topics: ['React Component Architecture', 'Event Filtering & Search', 'Interactive Pass Tier Selector', 'Dynamic QR Ticket Generation', 'Local Storage Pass Management'],
      features: ['Event discovery feed with date & category tags', 'VIP vs Standard pass tier selection UI', 'Digital QR code ticket preview modal', 'My Passes active ticket wallet dashboard']
    },
    {
      id: 'proj-5',
      title: 'TaskFlow - Todo & Task Manager Dashboard',
      category: 'major',
      tags: ['HTML', 'CSS', 'JS', 'Spring Boot', 'MySQL'],
      image: '/assets/full%20stack/TaskFlow-screen-1.png',
      moreCount: 0,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/TaskFlow-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/TaskFlow-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/TaskFlow-screen-3.png' },
        { label: 'Screen 4', url: '/assets/full%20stack/TaskFlow-screen-4.png' },
        { label: 'Screen 5', url: '/assets/full%20stack/TaskFlow-screen-5.png' },
      ],
      desc: 'Productivity dashboard for task tracking with status filters, priority levels, REST API sync, and persistent storage.',
      topics: ['REST API Integration', 'JSON Fetch & Async/Await', 'Full CRUD Operations', 'Status & Priority Filtering', 'Spring Boot & MySQL Sync'],
      features: ['Task status toggles (Pending, In Progress, Completed)', 'Priority level color tags', 'Spring Boot REST API synchronization', 'Persistent MySQL task database']
    },
    {
      id: 'proj-6',
      title: 'Apex Elite - SaaS Platform',
      category: 'major',
      tags: ['HTML', 'CSS', 'JS', 'Spring Boot', 'MySQL'],
      image: '/assets/full%20stack/Apex%20Elite-screen-1.png',
      moreCount: 2,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/Apex%20Elite-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/Apex%20Elite-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/Apex%20Elite-screen-3.png' },
        { label: 'Screen 4', url: '/assets/full%20stack/Apex%20Elite-screen-4.png' },
        { label: 'Screen 5', url: '/assets/full%20stack/Apex%20Elite-screen-5.png' },
      ],
      desc: 'Modern corporate SaaS platform dashboard featuring analytics widgets, user management, API integrations, and responsive UI.',
      topics: ['Dashboard UI Design', 'Data Visualization Widgets', 'Spring Boot Backend Integration', 'Session Auth & Roles', 'Relational Schema Design'],
      features: ['SaaS metric overview cards (MRR, Users, Conversion)', 'User access role management interface', 'Live analytics charting widgets', 'Settings configuration panel']
    },
    {
      id: 'proj-7',
      title: 'Expense Manager',
      category: 'major',
      tags: ['HTML', 'CSS', 'JS', 'React', 'Spring Boot', 'MySQL'],
      image: '/assets/full%20stack/Expense%20Manager-screen-1.png',
      moreCount: 3,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/Expense%20Manager-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/Expense%20Manager-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/Expense%20Manager-screen-3.png' },
        { label: 'Screen 4', url: '/assets/full%20stack/Expense%20Manager-screen-4.png' },
      ],
      desc: 'Personal financial management platform to track income, daily expenses, budget limits, category breakdown, and monthly reports.',
      topics: ['React Hooks (useState, useEffect, useMemo)', 'Spring Boot REST Controllers', 'Spring Data JPA & Hibernate', 'Financial Calculations', 'Modal Dialog Components'],
      features: ['Income vs Expense category breakdown', 'Monthly budget threshold progress bars', 'Date-range transaction history table', 'Visual summary reports']
    },
    {
      id: 'proj-8',
      title: 'AURA - Habit & Lifestyle Tracker',
      category: 'major',
      tags: ['HTML', 'CSS', 'JS', 'React', 'Spring Boot', 'MySQL'],
      image: '/assets/full%20stack/Aura-screen-1.png',
      moreCount: 2,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/Aura-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/Aura-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/Aura-screen-3.png' },
        { label: 'Screen 4', url: '/assets/full%20stack/Aura-screen-4.png' },
      ],
      desc: 'Daily habit formation & productivity tracker featuring streak tracking, completion analytics, custom goals, and reminder alerts.',
      topics: ['React State & Lifecycle', 'Component Reusability', 'Streak Counter Algorithms', 'Date Math & Calendar Grid', 'Spring Boot Persistence'],
      features: ['Daily habit check-off matrix', 'Current & longest streak counters', 'Completion percentage heatmaps', 'Custom habit creation modal']
    },
    {
      id: 'proj-9',
      title: 'TravelMate - Travel Planning Platform',
      category: 'major',
      tags: ['HTML', 'CSS', 'JS', 'React', 'Spring Boot', 'MySQL'],
      image: '/assets/full%20stack/Travel%20mate-Screen-1.png',
      moreCount: 6,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/Travel%20mate-Screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/Travel%20mate-Screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/Travel%20mate-Screen-3.png' },
        { label: 'Screen 4', url: '/assets/full%20stack/Travel%20mate-Screen-4.png' },
        { label: 'Screen 5', url: '/assets/full%20stack/Travel%20mate-Screen-5.png' },
      ],
      desc: 'All-in-one travel planner & booking dashboard for exploring destinations, itinerary scheduling, flight details, and hotel reservations.',
      topics: ['React Router Navigation', 'Multi-step Form Wizard', 'Spring Boot REST APIs', 'JPA One-to-Many Mappings', 'Third-Party API Integration'],
      features: ['Destination discovery cards with image gallery', 'Itinerary day-by-day planner widget', 'Hotel & flight cost estimator', 'Saved trips itinerary bookmarks']
    },
    {
      id: 'proj-10',
      title: 'FitCore - Fitness & Workout Platform',
      category: 'major',
      tags: ['HTML', 'CSS', 'JS', 'React', 'Spring Boot', 'MySQL'],
      image: '/assets/full%20stack/FitCore-screen-1.png',
      moreCount: 7,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/FitCore-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/FitCore-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/FitCore-screen-3.png' },
        { label: 'Screen 4', url: '/assets/full%20stack/FitCore-screen-4.png' },
      ],
      desc: 'Comprehensive fitness tracking portal to log workout sessions, track daily calorie intake, monitor health stats, and plan trainer routines.',
      topics: ['React Full Stack Architecture', 'Custom React Hooks', 'Spring Boot REST & Validation', 'Hibernate JPA Entities', 'Dynamic Charting UI'],
      features: ['Daily workout session log & exercise tracker', 'Calorie & macro counter widget', 'Personal fitness goal progress meters', 'Trainer routine schedule calendar']
    },

    // 11th & 12th Full Stack Projects
    {
      id: 'proj-11',
      title: 'Blinkit - Quick Commerce Grocery Delivery Platform',
      category: 'major',
      tags: ['HTML', 'CSS', 'JS', 'React', 'Spring Boot', 'MySQL'],
      image: '/assets/full%20stack/blinkit.png',
      moreCount: 0,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/blinkit.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/blinkit-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/blinkit-3.png' }
      ],
      desc: '10-minute quick commerce grocery delivery app with instant product search, category filters, cart checkout, and live order tracking.',
      topics: ['React Component Architecture', 'Quick Commerce UI Layouts', 'Dynamic Cart State Management', 'Spring Boot Order Processing', 'MySQL Relational Schema'],
      features: ['10-minute delivery search & category drawer', 'Product card with item increment/decrement buttons', 'Live cart order summary & bill breakdown', 'Real-time order dispatch tracking UI']
    },
    {
      id: 'proj-12',
      title: 'HealthCare Pulse - Medical & Doctor Appointment Portal',
      category: 'major',
      tags: ['HTML', 'CSS', 'JS', 'React', 'Spring Boot', 'MySQL'],
      image: '/assets/full%20stack/Health-care-screen-1.png',
      moreCount: 0,
      images: [
        { label: 'Screen 1', url: '/assets/full%20stack/Health-care-screen-1.png' },
        { label: 'Screen 2', url: '/assets/full%20stack/Health-care-screen-2.png' },
        { label: 'Screen 3', url: '/assets/full%20stack/Health-care-screen-3.png' },
      ],
      desc: 'Comprehensive healthcare portal for patient registration, specialist doctor appointment scheduling, medical prescription logs, and appointment slot booking.',
      topics: ['React Form Control & Calendar Grid', 'Doctor Consultation Slot Selector', 'Spring Boot REST & Security Auth', 'Patient Medical History Persistence', 'JPA Relationships'],
      features: ['Doctor specialty search & availability badge', 'Interactive date & time slot booking modal', 'Patient health history & prescription records', 'Consultation status dashboard']
    },

    // Backend Projects
    {
      id: 'proj-13',
      title: 'Campus Job Board',
      category: 'backend',
      tags: ['Servlet', 'JSP', 'Java', 'Tomcat'],
      image: '/assets/full%20stack/Campus%20job%20placement.jpeg',
      icon: 'work',
      desc: 'Students browse/apply for postings & recruiters post jobs. Server-rendered Servlet + JSP app with session tracking, filters, JSTL, and MVC architecture.',
      topics: ['Java Servlet Lifecycle (init, service, destroy)', 'JSP & JSTL Core Tags', 'HTTP Session Management', 'DAO Pattern & JDBC Connection Pooling', 'Tomcat Deployment'],
      features: ['Role-based access (Student vs Recruiter)', 'Multi-filter job search & application submission', 'Recruiter job posting management portal', 'Session-based user authentication']
    },
    {
      id: 'proj-14',
      title: 'Gym Membership Manager',
      category: 'backend',
      tags: ['Spring Core', 'Spring JDBC', 'Hibernate'],
      image: '/assets/full%20stack/Gym%20Membership%20Manager.jpeg',
      icon: 'fitness_center',
      desc: 'Container-focused member & attendance manager built with Spring IoC, 3-way container wiring (XML, Annotations, Java Config), SpEL, and Spring ORM.',
      topics: ['Spring IoC Container & Dependency Injection', '3-Way Bean Wiring (XML, Annotations, Java Config)', 'Spring Expression Language (SpEL)', 'Spring ORM & Hibernate Entity Manager', 'Spring Declarative Transactions'],
      features: ['Member registration & subscription tier tracking', 'Daily attendance logger engine', 'Spring JDBC Template batch updates', 'Hibernate ORM relational mapping (Member <-> Plan)']
    },
    {
      id: 'proj-15',
      title: 'Rental Listing Portal',
      category: 'backend',
      tags: ['Spring MVC', 'JSP', 'Hibernate', 'Tomcat'],
      image: '/assets/full%20stack/Rental%20Listing%20Portal.jpeg',
      icon: 'home_work',
      desc: 'Full rental property portal with photo uploads, search filters, owner dashboard, multi-step forms, binding validation, and custom interceptors.',
      topics: ['Spring MVC Architecture (@Controller, @RequestMapping)', 'Spring Form Binding & Validation (@Valid)', 'Custom Handler Interceptors', 'Multipart File Upload Handling', 'Hibernate Criteria API'],
      features: ['Property listing CRUD with multi-image uploads', 'Advanced property search with dynamic criteria', 'Property owner dashboard & lead manager', 'Custom interceptor for authentication checks']
    },
    {
      id: 'proj-16',
      title: 'Split-the-Bill REST API',
      category: 'backend',
      tags: ['Spring Boot', 'REST API', 'Validation', 'Swagger'],
      image: '/assets/full%20stack/Split-the-Bill%20REST%20API.jpeg',
      icon: 'receipt_long',
      desc: 'Group expense splitting REST API built with Spring Boot, custom validators, H2/MySQL profiles, OpenAPI/Swagger docs, DTOs, and error envelopes.',
      topics: ['Spring Boot Auto-configuration & Starters', 'RESTful API Design (@RestController)', 'DTO Pattern & Model Mapper', 'Custom Bean Validation (@Constraint)', 'OpenAPI 3 / Swagger Docs', 'Global Exception Handling (@ControllerAdvice)'],
      features: ['Expense splitting algorithm (Equal, Percentage, Exact)', 'Group balance settlement calculator', 'Swagger UI interactive API test bench', 'Centralized JSON error response envelope']
    },
    {
      id: 'proj-17',
      title: 'Agentic RAG Research Platform',
      category: 'backend',
      tags: ['Spring AI 2.0', 'Agentic RAG', 'PGVector', 'HyDE', 'Reranking'],
      image: '/assets/full%20stack/Agentic%20RAG%20Research%20Platform.jpeg',
      icon: 'psychology',
      desc: 'RAG pipeline engineering & agent orchestration platform featuring a 5-stage architecture: baseline RAG, 50-question evaluation harness, pipeline advisors (HyDE, Reranking), Spring AI 2.0 looping advisors, and smart single-hop/multi-hop query routing.',
      topics: [
        'Stage 1 — Baseline RAG Pipeline: Ingestion, Chunking comparison (Fixed-size vs Structural vs Semantic), Hybrid Search & Token Budgeting',
        'Stage 2 — 50-Question Evaluation Harness: Single-Hop (20), Multi-Hop (20), Unanswerable (10) measuring Recall@k, Precision@k, Faithfulness & Latency',
        'Stage 3 — Pipeline Improvements: Query Rewriting, Query Decomposition, HyDE (Hypothetical Embeddings) & Cross-Encoder Reranking Advisors',
        'Stage 4 — Spring AI 2.0 Agentic Loop: Plan ➔ Retrieve ➔ Assess Sufficiency ➔ Refine ➔ Terminate with multi-source routing, hop limits & trace capture',
        'Stage 5 — Smart Query Router: Classifying single-hop (fast baseline RAG) vs multi-hop (agentic loop) to optimize accuracy, cost & latency'
      ],
      features: [
        'Resumable multi-format document ingestion pipeline (PDF/HTML/Markdown) with metadata extraction & deduplication',
        'Automated 50-question evaluation benchmark measuring Recall@k, Precision@k, Answer Accuracy & Faithfulness',
        'Advisors chain featuring Query Rewriting, HyDE (Hypothetical Document Embeddings), and Cross-Encoder Reranking',
        'Spring AI 2.0 Looping Advisor with Plan ➔ Retrieve ➔ Assess Sufficiency ➔ Refine ➔ Terminate reasoning loop',
        'Production query classification router sending single-hop queries to baseline RAG and multi-hop queries to the Agent'
      ]
    },
    {
      id: 'proj-18',
      title: 'Spring AI: Document Intelligence Pipeline',
      category: 'backend',
      tags: ['Spring AI', 'Structured Output', 'LLM Benchmark', 'Async', 'RAG'],
      image: '/assets/full%20stack/Document%20Intelligence%20Pipeline.jpeg',
      icon: 'document_scanner',
      desc: 'Enterprise document processing pipeline that ingests messy invoices & receipts into typed Java POJOs, validates business rules & hallucinated figures, and benchmarks field-level accuracy across LLM providers.',
      topics: ['Structured Output (Typed Invoice & Line-Item POJOs)', 'Hallucination Detection & Strict Validation Rules', 'Confidence Scoring & Auto-Approve Routing Curve', 'Multi-Provider Benchmarking (Accuracy vs Cost per 1k docs)', 'Async Processing, Idempotency & Document Hash Caching', 'Versioned Prompts & Latency/Token Spend Observability'],
      features: ['Messy real-world invoice & receipt ingestion (30+ layout samples)', 'Typed Invoice POJO extraction (vendor, line items, tax, total)', 'Arithmetic & hallucination validator (catches model-invented values)', 'Auto-approve vs human review queue routing with confidence scoring', 'Multi-provider accuracy report & regression benchmark suite']
    }
  ], []);

  // Filtered Projects based on projectFilter state
  const displayedProjects = useMemo(() => {
    if (projectFilter === 'all') return allProjectsList;
    return allProjectsList.filter(p => p.category === projectFilter);
  }, [allProjectsList, projectFilter]);

  // Filtered Frontend modules by sub-category
  const displayedFrontendModules = useMemo(() => {
    if (frontendSubCategory === 'all') return frontendModules;
    return frontendModules.filter((m) => (m.category || '').toLowerCase() === frontendSubCategory.toLowerCase());
  }, [frontendModules, frontendSubCategory]);

  // Dynamic module counts for Frontend Categories
  const frontendCategoryCounts = useMemo(() => {
    const counts = { all: frontendModules.length, html: 0, css: 0, javascript: 0, react: 0 };
    frontendModules.forEach((m) => {
      const cat = (m.category || 'HTML').toLowerCase();
      if (counts[cat] !== undefined) counts[cat]++;
    });
    return counts;
  }, [frontendModules]);

  // Filtered Backend modules by sub-category
  const displayedBackendModules = useMemo(() => {
    if (backendSubCategory === 'all') return backendModules;
    if (backendSubCategory === 'jdbc-sql') {
      return backendModules.filter((m) => {
        const cat = (m.category || '').toLowerCase();
        return cat === 'jdbc' || cat === 'sql';
      });
    }
    if (backendSubCategory === 'spring-suite') {
      return backendModules.filter((m) => {
        const cat = (m.category || '').toLowerCase();
        return cat === 'spring-core' || cat === 'spring-mvc' || cat === 'spring-validation' || cat === 'spring-security';
      });
    }
    if (backendSubCategory === 'spring-boot-suite') {
      return backendModules.filter((m) => {
        const cat = (m.category || '').toLowerCase();
        return cat === 'spring-boot' || cat === 'adv-spring-boot';
      });
    }
    if (backendSubCategory === 'testing-logging') {
      return backendModules.filter((m) => {
        const cat = (m.category || '').toLowerCase();
        return cat === 'testing' || cat === 'logging';
      });
    }
    return backendModules.filter((m) => (m.category || '').toLowerCase() === backendSubCategory.toLowerCase());
  }, [backendModules, backendSubCategory]);

  // Dynamic module counts for Backend Categories
  const backendCategoryCounts = useMemo(() => {
    const counts = {
      all: backendModules.length,
      java: 0,
      'jdbc-sql': 0,
      jsp: 0,
      'spring-suite': 0,
      hibernate: 0,
      'spring-boot-suite': 0,
      'testing-logging': 0,
      'system-design': 0,
      microservices: 0,
      'spring-ai': 0,
    };
    backendModules.forEach((m) => {
      const cat = (m.category || 'java').toLowerCase();
      if (cat === 'java') counts.java++;
      else if (cat === 'jdbc' || cat === 'sql') counts['jdbc-sql']++;
      else if (cat === 'jsp') counts.jsp++;
      else if (['spring-core', 'spring-mvc', 'spring-validation', 'spring-security'].includes(cat)) counts['spring-suite']++;
      else if (cat === 'hibernate') counts.hibernate++;
      else if (['spring-boot', 'adv-spring-boot'].includes(cat)) counts['spring-boot-suite']++;
      else if (['testing', 'logging'].includes(cat)) counts['testing-logging']++;
      else if (cat === 'system-design') counts['system-design']++;
      else if (cat === 'microservices') counts.microservices++;
      else if (cat === 'spring-ai') counts['spring-ai']++;
    });
    return counts;
  }, [backendModules]);

  // Active track data for Syllabus
  const currentModules = activeTrack === 'frontend' ? displayedFrontendModules : displayedBackendModules;
  const accentColor = activeTrack === 'frontend' ? '#0083a0' : '#712ae2';
  const activeModule = currentModules[selectedTopicIndex] || currentModules[0] || { topics: [] };

  return (
    <div className="fsp-root">
      <Helmet>
        <title>Java Full Stack Development With Placement Assistance | Core2web</title>
        <meta
          name="description"
          content="Master React 19 & Spring Boot Java Full Stack Development with Placement Assistance at Core2web. Build real-world enterprise applications from scratch."
        />  <meta name="robots" content="index, follow" />

      </Helmet>

      {/* Top Navigation Bar */}
      <nav className="fsp-nav">
        <div className="fsp-nav-content">
          <a className="fsp-logo-link" href="/">
            <img
              alt="Core2web Logo"
              className="fsp-logo-img"
              src="/assets/images/landing/c2w-logo-full.svg"
            />
          </a>
          <div className="fsp-nav-right-group">
            <div className="fsp-nav-links">
              <button onClick={() => scrollToSection('overview')} className="fsp-nav-btn-link">Overview</button>
              <button onClick={() => scrollToSection('projects')} className="fsp-nav-btn-link">Projects</button>
              <button onClick={() => scrollToSection('curriculum')} className="fsp-nav-btn-link">Curriculum</button>
              <button onClick={() => scrollToSection('aptitude')} className="fsp-nav-btn-link">Aptitude</button>
              <button onClick={() => scrollToSection('mentorship')} className="fsp-nav-btn-link">Mentorship</button>
              <button onClick={() => scrollToSection('certification')} className="fsp-nav-btn-link">Certification</button>
              <button onClick={() => scrollToSection('internship')} className="fsp-nav-btn-link">Internship</button>
              <button onClick={() => scrollToSection('schedule')} className="fsp-nav-btn-link">Schedule</button>
              <button onClick={() => scrollToSection('pricing')} className="fsp-nav-btn-link">Pricing</button>
            </div>
            <button onClick={() => openEnrollModal('admission')} className="fsp-nav-enroll-btn">
              Enroll Now
            </button>
            {/* Mobile Hamburger Toggle Button */}
            <button
              className="fsp-nav-hamburger"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close Menu" : "Open Menu"}
            >
              <span className="material-symbols-outlined">
                {isMobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer / Dropdown */}
        {isMobileMenuOpen && (
          <div className="fsp-mobile-menu-drawer">
            <div className="fsp-mobile-menu-links">
              <button onClick={() => { scrollToSection('overview'); setIsMobileMenuOpen(false); }} className="fsp-mobile-nav-link">
                <span className="material-symbols-outlined">home</span>
                Overview
              </button>
              <button onClick={() => { scrollToSection('projects'); setIsMobileMenuOpen(false); }} className="fsp-mobile-nav-link">
                <span className="material-symbols-outlined">code_blocks</span>
                Projects (20+)
              </button>
              <button onClick={() => { scrollToSection('curriculum'); setIsMobileMenuOpen(false); }} className="fsp-mobile-nav-link">
                <span className="material-symbols-outlined">menu_book</span>
                Curriculum
              </button>
              <button onClick={() => { scrollToSection('aptitude'); setIsMobileMenuOpen(false); }} className="fsp-mobile-nav-link">
                <span className="material-symbols-outlined">psychology</span>
                Aptitude (300+)
              </button>
              <button onClick={() => { scrollToSection('mentorship'); setIsMobileMenuOpen(false); }} className="fsp-mobile-nav-link">
                <span className="material-symbols-outlined">groups</span>
                Mentorship
              </button>
              <button onClick={() => { scrollToSection('certification'); setIsMobileMenuOpen(false); }} className="fsp-mobile-nav-link">
                <span className="material-symbols-outlined">verified</span>
                Certification
              </button>
              <button onClick={() => { scrollToSection('internship'); setIsMobileMenuOpen(false); }} className="fsp-mobile-nav-link">
                <span className="material-symbols-outlined">badge</span>
                Internship
              </button>
              <button onClick={() => { scrollToSection('schedule'); setIsMobileMenuOpen(false); }} className="fsp-mobile-nav-link">
                <span className="material-symbols-outlined">schedule</span>
                Schedule
              </button>
              <button onClick={() => { scrollToSection('pricing'); setIsMobileMenuOpen(false); }} className="fsp-mobile-nav-link">
                <span className="material-symbols-outlined">payments</span>
                Pricing
              </button>
            </div>
            <div className="fsp-mobile-menu-actions">
              <button
                onClick={() => { openEnrollModal('admission'); setIsMobileMenuOpen(false); }}
                className="fsp-btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Enroll Now
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="fsp-hero-section" id="overview">
        <div className="fsp-hero-grid">
          <div>
            <div className="fsp-hero-badge">
              MOST AWAITED COURSE
            </div>
            <h1 className="fsp-hero-title">
              <span className="fsp-text-gradient">Java Full Stack Development</span>{' '}
              <span className="fsp-with-text">With</span>{' '}
              <span className="fsp-placement-highlight-animated">
                Placement Assistance
                <span className="fsp-placement-badge">100% Guidance</span>
              </span>
            </h1>
            <p className="fsp-hero-subtitle">React + Spring Boot + Spring AI</p>


            {/* Spring AI Featured Module Highlight Card */}
            <div className="fsp-hero-spring-ai-card">
              <img
                src="/assets/full%20stack/spring%20ai.png"
                alt="Spring AI Integration"
                className="fsp-spring-ai-banner-img"
              />
              <div className="fsp-spring-ai-info">
                <div className="fsp-spring-ai-pill">
                  <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>auto_awesome</span>
                  FEATURED HIGHLIGHT
                </div>
                <h4>Spring AI & LLM Apps Integration</h4>
                <p>Learn to integrate Generative AI, OpenAI APIs & Spring AI into enterprise full stack applications.</p>
              </div>
            </div>
            <div className="fsp-hero-price">
              ₹17,000 <span>+ 18% GST</span>
            </div>
            <div className="fsp-hero-actions">
              <button onClick={() => openEnrollModal('admission')} className="fsp-btn-primary">
                Enroll Now <span className="material-symbols-outlined">arrow_forward</span>
              </button>
              <button onClick={openSyllabusModal} className="fsp-btn-secondary">
                Download Syllabus PDF <span className="material-symbols-outlined">download</span>
              </button>
            </div>

            {/* Free Lecture & 5 Months Duration Announcement Bar - Below Buttons */}
            <div className="fsp-hero-announcement-bar fsp-announcement-bottom-left">
              <div className="fsp-announcement-item free-lecture">
                <span className="fsp-live-pulse-dot"></span>
                <span className="material-symbols-outlined icon icon-blue">event</span>
                <span className="fsp-label-text">Free Open to all Lectures: <strong className="fsp-highlight-blue">5, 6 & 7 October</strong></span>
              </div>
              <div className="fsp-announcement-divider"></div>
              <div className="fsp-announcement-item duration-item">
                <span className="material-symbols-outlined icon icon-purple">schedule</span>
                <span className="fsp-label-text">Duration: <strong className="fsp-highlight-purple">6 Months </strong></span>
              </div>
            </div>
          </div>

          {/* Hero Section Right Column - Static Course Preview Image */}
          <div className="fsp-hero-video-box">
            <div className="fsp-video-thumb-wrapper" style={{ cursor: 'default' }}>
              <img
                alt="Java Full Stack Development Course Architecture"
                className="fsp-video-thumb-img"
                src="/assets/images/courses/fullstack_thumbnail.png"
              />
            </div>
            <div className="fsp-video-info">
              <div className="fsp-video-badge">
                COURSE PREVIEW
              </div>
              <h3 className="fsp-video-title">Java Full Stack Development Architecture & Roadmap</h3>
              <p className="fsp-video-desc">Explore how React, Spring Boot, and MySQL connect seamlessly in enterprise applications.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section with Infinite One-by-One Border Glow & Sound Effect */}
      <section className="fsp-tech-section">
        <div className="fsp-section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '16px', color: '#00288e' }}>graphic_eq</span>
          TECHNOLOGIES YOU WILL MASTER
        </div>
        <div className="fsp-tech-flex">
          {techMasteryList.map((item, idx) => {
            const isActive = idx === activeTechIndex;
            return (
              <div
                key={idx}
                onMouseEnter={() => setActiveTechIndex(idx)}
                className={`fsp-tech-item ${isActive ? 'active-glow' : ''}`}
                style={{
                  color: item.textColor || item.color,
                  '--tech-border-color': item.color,
                  '--tech-glow-color': item.glowColor,
                  borderColor: isActive ? item.color : undefined
                }}
              >
                {item.name}
              </div>
            );
          })}
        </div>
      </section>

      {/* Learning Roadmap & Skill Equation Section */}
      <section className="fsp-roadmap-section" id="roadmap">
        <div className="fsp-section-header">
          <div className="fsp-hero-badge" style={{ margin: '0 auto 16px auto' }}>
            THE FULL STACK FORMULA
          </div>
          <h2 className="fsp-section-title">Frontend + Backend = Java Full Stack Developer</h2>
          <p className="fsp-section-subtitle">
            Master both ends of the engineering spectrum to build complete enterprise software solutions.
          </p>
        </div>

        <div className="fsp-roadmap-wrapper">
          {/* Top Animated Equation Row: Frontend + Backend = Java Full Stack */}
          <div className="fsp-eq-container">
            {/* Frontend Skills Card */}
            <div className="fsp-eq-card frontend-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '28px', color: '#0083a0' }}>devices</span>
                <span style={{ fontSize: '12px', fontWeight: '800', color: '#0083a0', letterSpacing: '0.05em' }}>TRACK 01</span>
              </div>
              <h3 className="fsp-eq-title">Frontend Skills</h3>
              <p className="fsp-eq-desc">React, JavaScript, HTML5 & CSS3</p>

              <div className="fsp-bar-outer">
                <div className="fsp-bar-inner" style={{ width: '100%', background: 'linear-gradient(90deg, #0083a0 0%, #61daf1 100%)' }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '700', color: '#0083a0' }}>
                <span>Mastery Status</span>
                <span>100% Complete</span>
              </div>
            </div>

            {/* Plus Symbol (+) */}
            <div className="fsp-eq-symbol">+</div>

            {/* Backend Skills Card */}
            <div className="fsp-eq-card backend-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '28px', color: '#4c8427' }}>dns</span>
                <span style={{ fontSize: '12px', fontWeight: '800', color: '#4c8427', letterSpacing: '0.05em' }}>TRACK 02</span>
              </div>
              <h3 className="fsp-eq-title">Backend Skills</h3>
              <p className="fsp-eq-desc">Java Core, Spring, Spring Boot, Spring AI, REST APIs & SQL</p>

              <div className="fsp-bar-outer">
                <div className="fsp-bar-inner" style={{ width: '100%', background: 'linear-gradient(90deg, #4c8427 0%, #6db33f 100%)' }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '700', color: '#4c8427' }}>
                <span>Mastery Status</span>
                <span>100% Complete</span>
              </div>
            </div>

            {/* Equals Symbol (=) */}
            <div className="fsp-eq-symbol master-symbol">=</div>

            {/* Java Full Stack Developer Card */}
            <div className="fsp-eq-card master-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '28px', color: '#feef00' }}>workspace_premium</span>
                <span style={{ fontSize: '12px', fontWeight: '800', color: '#feef00', letterSpacing: '0.05em' }}>ULTIMATE GOAL</span>
              </div>
              <h3 className="fsp-eq-title master-title">Java Full Stack Developer</h3>
              <p className="fsp-eq-desc master-desc">Enterprise Architect & Full Stack Engineer</p>

              <div className="fsp-bar-outer" style={{ background: 'rgba(255,255,255,0.2)' }}>
                <div className="fsp-bar-inner" style={{ width: '100%', background: 'linear-gradient(90deg, #feef00 0%, #ff8c00 100%)' }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '700', color: '#feef00' }}>
                <span>Job Ready Status</span>
                <span>100% Hired Grade 🚀</span>
              </div>
            </div>
          </div>

          {/* Bottom Step Flow Grid */}
          <div className="fsp-step-flow-grid">
            {[
              { num: '01', title: 'Frontend Core', desc: 'HTML, CSS, JavaScript' },
              { num: '02', title: 'React.js', desc: 'Components, State, Hooks, UI' },
              { num: '03', title: 'Java Core + Advanced', desc: 'OOPs, Collections, Multithreading, JDBC, JSP Servlet' },
              { num: '04', title: 'Spring', desc: 'Core, IoC, Dependency Injection, MVC' },
              { num: '05', title: 'Spring Boot', desc: 'REST APIs, JPA, Security' },
              { num: '06', title: 'Full Stack', desc: 'Integration, Deployment, Projects', isHighlight: true },
            ].map((item, idx) => {
              const isCurrentStep = idx === activeTechIndex % 6;
              return (
                <div
                  key={idx}
                  className={`fsp-step-card ${isCurrentStep ? 'active-step' : ''} ${item.isHighlight ? 'highlight' : ''}`}
                >
                  <div className="fsp-roadmap-circle" style={item.isHighlight ? { background: '#712ae2' } : {}}>
                    {item.num}
                  </div>
                  <h3 className={`fsp-step-title ${item.isHighlight ? 'highlight-step-title' : ''}`}>
                    {item.title}
                  </h3>
                  <p className="fsp-step-desc">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You'll Learn Section */}
      <section className="fsp-learn-section">
        <div className="fsp-container">
          <div className="fsp-section-header">
            <h2 className="fsp-section-title">What You'll Learn</h2>
            <p className="fsp-section-subtitle">Comprehensive skills to build end-to-end applications.</p>
          </div>
          <div className="fsp-learn-grid">
            {[
              { icon: 'web', title: 'Responsive UIs', desc: 'Build beautiful, interactive web interfaces using modern React practices.' },
              { icon: 'api', title: 'RESTful APIs', desc: 'Design and implement scalable APIs with Spring Boot and Java.' },
              { icon: 'database', title: 'Database Management', desc: 'Model data, write efficient SQL queries, and manage relational databases.' },
              { icon: 'security', title: 'Authentication & Authorization', desc: 'Implement secure login systems using JWT, Spring Security, and Role-Based Access Control (RBAC).' },
              { icon: 'integration_instructions', title: 'System Integration', desc: 'Seamlessly connect React frontends to Spring Boot backends.' },
              { icon: 'cloud_upload', title: 'Deployment', desc: 'Learn how to deploy full stack applications to cloud platforms.' },
            ].map((item, idx) => (
              <div key={idx} className="fsp-learn-card">
                <span className="material-symbols-outlined fsp-learn-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It All Works Together Section */}
      <section className="fsp-how-section">
        <div className="fsp-section-header">
          <h2 className="fsp-section-title">How It All Works Together</h2>
          <p className="fsp-section-subtitle">Understanding the architecture of a modern full stack application.</p>
        </div>
        <div className="fsp-how-container">
          <div className="fsp-how-node">
            <span className="material-symbols-outlined" style={{ fontSize: '36px', color: '#444653' }}>person</span>
            <span style={{ fontWeight: '700', marginTop: '8px' }}>User</span>
          </div>
          <span className="material-symbols-outlined fsp-how-arrow">arrow_forward</span>
          <div className="fsp-how-node" style={{ borderBottom: '4px solid #0083a0' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '36px', color: '#0083a0' }}>web</span>
            <span style={{ fontWeight: '700', marginTop: '8px' }}>React App</span>
            <span style={{ fontSize: '11px', color: '#444653' }}>Browser / Client</span>
          </div>
          <div className="fsp-how-connector">
            <span className="fsp-how-badge">REST API</span>
            <span className="material-symbols-outlined fsp-how-arrow" style={{ fontSize: '20px' }}>sync_alt</span>
            <span className="fsp-how-badge">JSON</span>
          </div>
          <div className="fsp-how-node" style={{ borderBottom: '4px solid #4c8427' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '36px', color: '#4c8427' }}>dns</span>
            <span style={{ fontWeight: '700', marginTop: '8px' }}>Spring Boot</span>
            <span style={{ fontSize: '11px', color: '#444653' }}>Server / Logic</span>
          </div>
          <span className="material-symbols-outlined fsp-how-arrow">sync_alt</span>
          <div className="fsp-how-node" style={{ borderBottom: '4px solid #b56800' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '36px', color: '#b56800' }}>database</span>
            <span style={{ fontWeight: '700', marginTop: '8px' }}>MySQL</span>
            <span style={{ fontSize: '11px', color: '#444653' }}>Database</span>
          </div>
        </div>
      </section>

      {/* Build Real-World Projects Section (Dynamic & Categorized) */}
      <section className="fsp-projects-section" id="projects">
        <div className="fsp-container">
          <div className="fsp-section-header">
            <h2 className="fsp-section-title">Build Real-World Projects ({allProjectsList.length}+ Projects)</h2>
            <p className="fsp-section-subtitle">Apply what you learn by building full-fledged applications from scratch.</p>
          </div>

          {/* Project Category Filter Pills */}
          <div className="fsp-project-tabs">
            <button
              onClick={() => setProjectFilter('all')}
              className={`fsp-project-tab-btn ${projectFilter === 'all' ? 'active' : ''}`}
            >
              All Projects ({allProjectsList.length})
            </button>
            <button
              onClick={() => setProjectFilter('major')}
              className={`fsp-project-tab-btn ${projectFilter === 'major' ? 'active' : ''}`}
            >
              Full Stack Web Apps ({allProjectsList.filter(p => p.category === 'major').length})
            </button>
            <button
              onClick={() => setProjectFilter('frontend')}
              className={`fsp-project-tab-btn ${projectFilter === 'frontend' ? 'active' : ''}`}
            >
              Frontend Websites ({allProjectsList.filter(p => p.category === 'frontend').length})
            </button>
            <button
              onClick={() => setProjectFilter('backend')}
              className={`fsp-project-tab-btn ${projectFilter === 'backend' ? 'active' : ''}`}
            >
              Backend Specific ({allProjectsList.filter(p => p.category === 'backend').length})
            </button>
          </div>

          {/* Projects Grid */}
          <div className="fsp-projects-grid">
            {displayedProjects.map((project, idx) => (
              <div
                key={project.id || idx}
                className="fsp-project-card"
                onClick={() => openProjectModal(project)}
              >
                <div className="fsp-project-header-img">
                  {project.image ? (
                    <img src={project.image} alt={project.title} className="fsp-project-img" />
                  ) : (
                    <div className="fsp-project-icon-wrapper">
                      <span className="material-symbols-outlined fsp-project-icon">
                        {project.icon || 'code'}
                      </span>
                    </div>
                  )}
                  <span className={`fsp-project-cat-badge ${project.category}`}>
                    {project.category === 'major'
                      ? 'Full Stack Major'
                      : project.category === 'frontend'
                        ? 'Frontend App'
                        : 'Backend'}
                  </span>
                  {project.images && project.images.length > 2 && (
                    <div className="fsp-card-screens-badge">
                      <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>collections</span>
                      <span>+More</span>
                    </div>
                  )}
                </div>
                <div className="fsp-project-body">
                  <div>
                    <div style={{ marginBottom: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {project.tags.map((tag, tIdx) => {
                        const isFrontendTag = ['HTML', 'CSS', 'JS', 'React', 'HTML5', 'CSS3', 'JavaScript'].includes(tag);
                        return (
                          <span key={tIdx} className={isFrontendTag ? 'fsp-tag-frontend' : 'fsp-tag-backend'}>
                            {tag}
                          </span>
                        );
                      })}
                    </div>
                    <h3>{project.title}</h3>
                    <p>{project.desc}</p>
                  </div>
                  <button
                    className="fsp-project-view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openProjectModal(project);
                    }}
                  >
                    <span>View Details</span>
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                      arrow_forward
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Process & Highlights Section */}
      <section className="fsp-process-section" id="benefits">
        <div>
          <h2 className="fsp-section-title" style={{ textAlign: 'left', marginBottom: '32px' }}>The Core2web Learning Process</h2>
          <div>
            {[
              { num: '01', title: 'Concept Mastery', desc: "Deep dive into theoretical concepts to understand the 'why' before the 'how'." },
              { num: '02', title: 'Hands-on Coding', desc: 'Live coding sessions and assignments to build muscle memory.' },
              { num: '03', title: 'Project Building', desc: 'Synthesize your knowledge by building complete applications.' },
              { num: '04', title: 'Interview Prep', desc: 'Mock interviews, resume building, profile building & placement assistance.' },
            ].map((step, idx) => (
              <div key={idx} className="fsp-process-step">
                <div className="fsp-process-num">{step.num}</div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '700' }}>{step.title}</h4>
                  <p style={{ margin: 0, color: '#444653', fontSize: '14px' }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #c4c5d5' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>Course Highlights</h3>
            <div className="fsp-highlights-grid">
              {highlights.map((h, idx) => (
                <div key={idx} className="fsp-highlight-card">
                  <span className="fsp-highlight-val">{h.value}</span>
                  <span className="fsp-highlight-title">{h.title}</span>
                  <span className="fsp-highlight-desc">{h.description}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Architecture Visual Card Box + Skills Box */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Architecture Visual Box */}
          <div className="fsp-arch-box">
            {/* FRONTEND React Card */}
            <div className="fsp-glass-card">
              <div style={{ background: 'rgba(97,218,251,0.15)', padding: '10px', borderRadius: '10px', color: '#0083a0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '32px' }}>devices</span>
              </div>
              <div>
                <p style={{ fontSize: '11px', fontWeight: '700', color: '#444653', letterSpacing: '0.05em', margin: 0 }}>FRONTEND</p>
                <p style={{ fontSize: '18px', fontWeight: '700', color: '#111c2d', margin: 0 }}>React</p>
              </div>
            </div>

            {/* Vertical Channel: Downward Request Arrow -> REST API Pill -> Upward Response Arrow */}
            <div className="fsp-arch-vert-channel">
              <div className="fsp-vert-line">
                <span className="fsp-vert-dot req-vert-dot"></span>
                <span className="material-symbols-outlined fsp-vert-arrow-icon down">south</span>
              </div>

              <div className="fsp-arch-badge-pill">
                <span className="material-symbols-outlined fsp-sync-spin">swap_vert</span>
                <span>REST API</span>
              </div>

              <div className="fsp-vert-line">
                <span className="fsp-vert-dot res-vert-dot"></span>
                <span className="material-symbols-outlined fsp-vert-arrow-icon up">north</span>
              </div>
            </div>

            {/* SINGLE DIV COMBINED BACKEND + DATABASE CARD (Spring Boot + MySQL) */}
            <div className="fsp-glass-card combined-backend-card">
              {/* Sub-row 1: Spring Boot Backend */}
              <div className="fsp-backend-sub-row">
                <div style={{ background: 'rgba(109,179,63,0.15)', padding: '10px', borderRadius: '10px', color: '#4c8427', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '32px' }}>dns</span>
                </div>
                <div>
                  <p style={{ fontSize: '11px', fontWeight: '700', color: '#4c8427', letterSpacing: '0.05em', margin: 0 }}>BACKEND</p>
                  <p style={{ fontSize: '18px', fontWeight: '700', color: '#111c2d', margin: 0 }}>Spring Boot</p>
                </div>
              </div>

              {/* Sub-row Divider */}
              <div className="fsp-backend-divider"></div>

              {/* Sub-row 2: MySQL Database */}
              <div className="fsp-backend-sub-row">
                <div style={{ background: 'rgba(242,145,17,0.15)', padding: '10px', borderRadius: '10px', color: '#b56800', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '32px' }}>database</span>
                </div>
                <div>
                  <p style={{ fontSize: '11px', fontWeight: '700', color: '#b56800', letterSpacing: '0.05em', margin: 0 }}>DATABASE</p>
                  <p style={{ fontSize: '18px', fontWeight: '700', color: '#111c2d', margin: 0 }}>MySQL</p>
                </div>
              </div>
            </div>
          </div>

          <div className="fsp-skills-box">
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px' }}>Skills That Get You Hired</h3>
            <div>
              {[
                'HTML', 'CSS', 'JavaScript', 'React.js', 'Redux', 'Spring Boot', 'REST APIs', 'Java', 'MySQL',
                'Authentication & Authorization (RBAC)', 'Git', 'Hibernate / JPA', 'Spring AI', 'Spring Security',
                'Problem Solving'
              ].map((skill, idx) => (
                <span key={idx} className="fsp-skill-pill">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
         DARK THEME COURSE SYLLABUS SECTION WITH FRONTEND / BACKEND TABS
         ========================================================================== */}
      <section className="fsp-container" id="curriculum">
        <div className="fsp-syll-dark-wrapper">
          {/* Section Header */}
          <div className="fsp-syll-header">
            <div className="fsp-syll-header-flex">
              <div>
                <h2 className="fsp-syll-title">Course Syllabus</h2>
                <p className="fsp-syll-subtitle">
                  Comprehensive curriculum designed for mastery. Select a module to explore.
                </p>
              </div>
              <button
                type="button"
                onClick={openSyllabusModal}
                className="fsp-download-syllabus-btn"
                style={{ cursor: 'pointer', border: 'none' }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>download</span>
                Download Syllabus PDF
              </button>
            </div>
          </div>

          {/* 4 Highlights Stats Pills Row */}
          <div className="fsp-syll-stats-grid">
            <div className="fsp-syll-stat-pill">
              <span className="material-symbols-outlined fsp-syll-stat-icon">quiz</span>
              <div>
                <div className="fsp-syll-stat-num">2000+</div>
                <div className="fsp-syll-stat-label">MCQs</div>
                <div className="fsp-syll-stat-desc">Strengthen concepts with topic-wise practice questions.</div>
              </div>
            </div>

            <div className="fsp-syll-stat-pill">
              <span className="material-symbols-outlined fsp-syll-stat-icon">code_blocks</span>
              <div>
                <div className="fsp-syll-stat-num">18+</div>
                <div className="fsp-syll-stat-label">Projects</div>
                <div className="fsp-syll-stat-desc">Build practical projects across frontend and backend development.</div>
              </div>
            </div>

            <div className="fsp-syll-stat-pill">
              <span className="material-symbols-outlined fsp-syll-stat-icon">assignment_turned_in</span>
              <div>
                <div className="fsp-syll-stat-num">20+</div>
                <div className="fsp-syll-stat-label">Topic-Based Assignments</div>
                <div className="fsp-syll-stat-desc">Practice important concepts through topic-based assignments.</div>
              </div>
            </div>

            <div className="fsp-syll-stat-pill">
              <span className="material-symbols-outlined fsp-syll-stat-icon">terminal</span>
              <div>
                <div className="fsp-syll-stat-num">Daily</div>
                <div className="fsp-syll-stat-label">Practical Sessions</div>
                <div className="fsp-syll-stat-desc">Daily hands-on practicals to turn concepts into coding experience.</div>
              </div>
            </div>
          </div>

          {/* FRONTEND & BACKEND TOGGLE BUTTONS (Default Open: Frontend) */}
          <div className="fsp-track-switcher-container">
            <div className="fsp-track-switcher-box">
              <button
                onClick={() => handleTrackChange('frontend')}
                className={`fsp-track-switcher-btn ${activeTrack === 'frontend' ? 'active-frontend' : ''}`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>devices</span>
                Frontend Syllabus ({frontendModules.length} Modules)
              </button>
              <button
                onClick={() => handleTrackChange('backend')}
                className={`fsp-track-switcher-btn ${activeTrack === 'backend' ? 'active-backend' : ''}`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>dns</span>
                Backend Syllabus ({backendModules.length} Modules)
              </button>
            </div>
          </div>

          {/* FRONTEND SUB-CATEGORY PILL TABS (HTML, CSS, JavaScript, React) */}
          {activeTrack === 'frontend' && (
            <div className="fsp-subcat-container">
              {[
                { id: 'all', label: 'All Modules', icon: 'apps', count: frontendCategoryCounts.all, color: '#0083a0' },
                { id: 'html', label: 'HTML', icon: 'code', count: frontendCategoryCounts.html, color: '#e34f26' },
                { id: 'css', label: 'CSS', icon: 'style', count: frontendCategoryCounts.css, color: '#1572b6' },
                { id: 'javascript', label: 'JavaScript', icon: 'javascript', count: frontendCategoryCounts.javascript, color: '#f7df1e' },
                { id: 'react', label: 'React', icon: 'deployed_code', count: frontendCategoryCounts.react, color: '#61dafb' },
              ].map((cat) => {
                const isActive = frontendSubCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleFrontendCategoryChange(cat.id)}
                    className={`fsp-subcat-pill ${isActive ? 'active' : ''}`}
                    style={
                      isActive
                        ? {
                          borderColor: cat.color,
                          background: cat.id === 'javascript' ? 'rgba(247, 223, 30, 0.18)' : `${cat.color}22`,
                          boxShadow: `0 0 16px ${cat.color}33`,
                        }
                        : {}
                    }
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px', color: cat.color }}>
                      {cat.icon}
                    </span>
                    <span className="fsp-subcat-label">{cat.label}</span>
                    <span
                      className="fsp-subcat-badge"
                      style={{
                        background: isActive ? cat.color : 'rgba(255, 255, 255, 0.1)',
                        color: isActive && cat.id === 'javascript' ? '#000000' : '#ffffff',
                      }}
                    >
                      {cat.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* BACKEND SUB-CATEGORY PILL TABS */}
          {activeTrack === 'backend' && (
            <div className="fsp-subcat-container">
              <Helmet>
                      {/* 
                                  Purpose: Defines the page title displayed in search results and browser tabs. 
                                           It’s one of the most important SEO elements. 
                                  */}
                      <title>{`Project Implementation Event`}</title>
              
                      {/* 
                                  Purpose: Provides a brief summary of the page content in search results. 
                                           While not a direct ranking factor, it influences CTR. 
                                  */}
                      <meta name="description" content="" />
              
                      {/* Purpose: Controls indexing and link-following behavior. */}
                      <meta name="robots" content="index, follow" />
              
                    </Helmet>
              {[
                { id: 'all', label: 'All Modules', icon: 'apps', count: backendCategoryCounts.all, color: '#712ae2' },
                { id: 'java', label: 'Java', icon: 'code', count: backendCategoryCounts.java, color: '#e34f26' },
                { id: 'jdbc-sql', label: 'JDBC, SQL & Database', icon: 'database', count: backendCategoryCounts['jdbc-sql'], color: '#f29111' },
                { id: 'jsp', label: 'JSP / Servlets', icon: 'web_asset', count: backendCategoryCounts.jsp, color: '#a855f7' },
                { id: 'spring-suite', label: 'Spring, Spring Security & REST API', icon: 'shield_lock', count: backendCategoryCounts['spring-suite'], color: '#22c55e' },
                { id: 'hibernate', label: 'Hibernate / JPA', icon: 'layers', count: backendCategoryCounts.hibernate, color: '#ec4899' },
                { id: 'spring-boot-suite', label: 'Spring Boot & Advanced Spring Boot', icon: 'bolt', count: backendCategoryCounts['spring-boot-suite'], color: '#06b6d4' },
                { id: 'testing-logging', label: 'Testing, Logging & Monitoring', icon: 'science', count: backendCategoryCounts['testing-logging'], color: '#f97316' },
                { id: 'system-design', label: 'System Design', icon: 'architecture', count: backendCategoryCounts['system-design'], color: '#6366f1' },
                { id: 'microservices', label: 'Microservices', icon: 'hub', count: backendCategoryCounts.microservices, color: '#14b8a6' },
                { id: 'spring-ai', label: 'Spring AI', icon: 'auto_awesome', count: backendCategoryCounts['spring-ai'], color: '#00e676' },
              ].map((cat) => {
                const isActive = backendSubCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleBackendCategoryChange(cat.id)}
                    className={`fsp-subcat-pill ${isActive ? 'active' : ''}`}
                    style={
                      isActive
                        ? {
                          borderColor: cat.color,
                          background: `${cat.color}22`,
                          boxShadow: `0 0 16px ${cat.color}33`,
                        }
                        : {}
                    }
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px', color: cat.color }}>
                      {cat.icon}
                    </span>
                    <span className="fsp-subcat-label">{cat.label}</span>
                    <span
                      className="fsp-subcat-badge"
                      style={{
                        background: isActive ? cat.color : 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                      }}
                    >
                      {cat.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Master-Detail Layout for Active Track */}
          <div className="fsp-syll-main-grid">
            {/* Left Sidebar (Topics List) */}
            <div className="fsp-syll-sidebar">
              {currentModules.map((mod, idx) => {
                const isActive = idx === selectedTopicIndex;
                const catColorMap = {
                  HTML: '#e34f26',
                  CSS: '#1572b6',
                  JavaScript: '#f7df1e',
                  React: '#61dafb',
                  java: '#e34f26',
                  sql: '#f29111',
                  jdbc: '#eab308',
                  jsp: '#a855f7',
                  git: '#84cc16',
                  'spring-core': '#22c55e',
                  'spring-boot': '#06b6d4',
                  'spring-mvc': '#3b82f6',
                  'spring-validation': '#8b5cf6',
                  hibernate: '#ec4899',
                  'spring-security': '#ef4444',
                  'adv-spring-boot': '#10b981',
                  testing: '#f97316',
                  logging: '#64748b',
                  'system-design': '#6366f1',
                  microservices: '#14b8a6',
                  'spring-ai': '#00e676',
                };
                const tagColor = activeTrack === 'frontend' ? (catColorMap[mod.category] || '#0083a0') : (catColorMap[mod.category] || '#712ae2');

                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedTopicIndex(idx)}
                    className={`fsp-syll-topic-item ${isActive ? 'active' : ''}`}
                    style={isActive ? { borderColor: tagColor } : {}}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span className="fsp-syll-topic-tag">
                        Module {mod.moduleNum || idx + 1}
                      </span>
                      <span
                        className="fsp-cat-tag-pill"
                        style={{
                          backgroundColor: `${tagColor}20`,
                          color: mod.category === 'JavaScript' || mod.category === 'javascript' ? '#f7df1e' : tagColor,
                          border: `1px solid ${tagColor}40`,
                          textDecoration: 'underline',
                          textUnderlineOffset: '3px',
                          textDecorationThickness: '2px',
                          fontWeight: '800'
                        }}
                      >
                        {mod.category ? mod.category.toUpperCase() : 'MODULE'}
                      </span>
                    </div>
                    <h4 className="fsp-syll-topic-name">{mod.title}</h4>
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>
                        {mod.topics?.length || 0} Sub-Topics
                      </span>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px', color: isActive ? tagColor : '#64748b' }}>
                        arrow_forward_ios
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Detail Panel */}
            <div className="fsp-syll-detail-panel">
              {/* Sub-topics Heading */}
              <div className="fsp-syll-detail-header" style={{ marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#ffffff', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className="material-symbols-outlined" style={{ color: accentColor }}>auto_stories</span>
                    {activeModule.title || 'Sub-Topics Covered'}
                  </h3>
                  <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>
                    Detailed concepts & practical topics covered in this module.
                  </p>
                </div>
                <span style={{ background: accentColor, color: '#ffffff', fontSize: '12px', fontWeight: '800', padding: '4px 14px', borderRadius: '9999px', boxShadow: `0 4px 14px ${accentColor}40` }}>
                  {activeModule.topics?.length || 0} Sub-Topics
                </span>
              </div>

              {/* List of Sub-topics */}
              <div className="fsp-syll-items-list" ref={itemsListRef}>
                {activeModule.topics.map((topicStr, idx) => (
                  <div key={idx} className="fsp-syll-item-row">
                    <div className="fsp-syll-item-left">
                      <div className="fsp-syll-icon-box" style={{ background: accentColor }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>present_to_all</span>
                      </div>
                      <h4 className="fsp-syll-item-title">{topicStr}</h4>
                    </div>
                    {/* <div className="fsp-syll-play-btn">
                      <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>play_arrow</span>
                    </div> */}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Super-X Section */}
      <section className="fsp-superx-section" id="super-x">
        <div className="fsp-container">
          <div className="fsp-superx-header">
            <div className="fsp-superx-badge">
              <img src="/assets/superx/images/C2wSuperx.png" alt="Super-X" style={{ height: '22px', width: 'auto' }} />
              <span>Being Innovative towards the Projects World!!</span>
            </div>
            <h1 className="fsp-superx-logo-title">Super-X</h1>
            <h2 className="fsp-superx-subtitle">Turn Your Idea Into a Real Product.</h2>
            <p className="fsp-superx-desc">
              Experience the journey from ideation to launch with expert guidance at every step.
            </p>
          </div>

          <div className="fsp-superx-grid">
            {[
              {
                title: 'Your Idea',
                desc: 'We help you validate and refine your concept into a viable technical blueprint.',
                img: '/assets/full%20stack/idea-thumbnail.png',
                alt: 'Turn Your Idea into Technical Blueprint'
              },
              {
                title: 'Your Team',
                desc: 'Collaborate with peers, distribute roles, and build a high-functioning engineering squad.',
                img: '/assets/full%20stack/team-tumbnail.png',
                alt: 'Build a High-Functioning Engineering Team'
              },
              {
                title: 'Your Product',
                desc: 'Deploy a fully functional, production-ready application to real users.',
                img: '/assets/full%20stack/product-thumbnail.png',
                alt: 'Deploy Production-Ready Product'
              }
            ].map((card, idx) => (
              <div key={idx} className="fsp-superx-card">
                <div className="fsp-superx-img-wrapper">
                  <img
                    src={card.img}
                    alt={card.alt}
                    className="fsp-superx-card-img"
                  />
                </div>
                <div className="fsp-superx-card-body">
                  <h3 className="fsp-superx-card-title">{card.title}</h3>
                  <p className="fsp-superx-card-desc">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Super-X Combined Showcase Banner */}
          <div className="fsp-superx-gallery-container">
            <div className="fsp-superx-gallery-header">
              <h3 className="fsp-superx-gallery-title">
                Previous Super-X Editions & Project Showcase
              </h3>
              <p className="fsp-superx-gallery-subtitle">
                Explore all past Super-X editions and product hackathons powered by Incubators Systems by Core2web.
              </p>
            </div>

            <div
              className="fsp-superx-combined-card"
              onClick={() => navigate('/superx')}
            >
              <div className="fsp-superx-combined-img-wrapper">
                <img
                  src="/assets/superx/images/superxBatches/superx_all_editions_combined.png"
                  alt="Super-X Editions 4.0, 3.0, 2.0 & 1.0 Combined"
                  className="fsp-superx-combined-img"
                />
              </div>
              <div className="fsp-superx-combined-caption">
                <div className="fsp-superx-caption-left">
                  <img src="/assets/superx/images/C2wSuperx.png" alt="Super-X Logo" className="fsp-superx-caption-logo" />
                  <span className="fsp-superx-caption-title">Editions & Hackathons</span>
                </div>
                <div className="fsp-superx-caption-btn">
                  <span>Explore All Editions</span>
                  <span className="material-symbols-outlined icon-arrow">arrow_forward</span>
                </div>
              </div>
            </div>
          </div>

          {/* Flagship Production SaaS Showcase Carousel: The Subscription & playZ */}
          <div className="fsp-flagship-carousel-wrapper">
            <div className="fsp-flagship-heading-bar">
              <h2 className="fsp-flagship-section-title">
                Ideas Turn Into <span className="fsp-text-gradient">Startups</span>
              </h2>
              <p className="fsp-flagship-section-subtitle">
                Core2web actively backs and incubates student-built ideas into real production startups.
              </p>
            </div>

            <div
              className="fsp-flagship-viewport"
              onMouseEnter={() => setIsHoveringFlagship(true)}
              onMouseLeave={() => setIsHoveringFlagship(false)}
              onTouchStart={handleFlagshipTouchStart}
              onTouchEnd={handleFlagshipTouchEnd}
            >
              <div
                className="fsp-flagship-track"
                style={{ transform: `translateX(-${flagshipIndex * 100}%)` }}
              >
                {flagshipCardsData.map((card, idx) => (
                  <div key={card.id} className="fsp-subscription-showcase-box">
                    <div className="fsp-sub-showcase-left">
                      <div className="fsp-sub-tag">
                        <span className="material-symbols-outlined" style={{ fontSize: '15px', color: '#10b981' }}>verified</span>
                        {card.tag}
                      </div>
                      <h3 className="fsp-sub-title">
                        {card.title}{' '}
                        {card.urlPill && (
                          <span className="fsp-sub-link-pill">{card.urlPill}</span>
                        )}
                      </h3>
                      <p
                        className="fsp-sub-desc"
                        dangerouslySetInnerHTML={{ __html: card.desc }}
                      />
                      {card.url && (
                        <div className="fsp-sub-actions">
                          <a
                            href={card.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="fsp-btn-visit-sub"
                          >
                            <span>{card.btnText}</span>
                            <span className="material-symbols-outlined icon-arrow">open_in_new</span>
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="fsp-sub-showcase-right">
                      <div className="fsp-sub-image-card">
                        {isPlayingFlagshipVideo && flagshipIndex === idx && card.videoUrl ? (
                          <div className="fsp-sub-video-wrapper">
                            <iframe
                              src={card.videoUrl}
                              title={`Core2web Talks - ${card.title}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              className="fsp-sub-iframe"
                            ></iframe>
                            <button
                              type="button"
                              onClick={() => setIsPlayingFlagshipVideo(false)}
                              className="fsp-sub-close-video-btn"
                              title="Close Video"
                            >
                              <span className="material-symbols-outlined">close</span>
                            </button>
                          </div>
                        ) : (
                          <div
                            className="fsp-sub-thumbnail-container"
                            style={{ cursor: card.videoUrl ? 'pointer' : 'default' }}
                            onClick={() => {
                              if (card.videoUrl) {
                                setIsPlayingFlagshipVideo(true);
                              }
                            }}
                          >
                            <img
                              src={card.image}
                              alt={`${card.title} - Core2Web Incubated Product`}
                              className="fsp-sub-showcase-img"
                            />
                            {card.videoUrl && (
                              <div className="fsp-video-play-overlay">
                                <div className="fsp-play-btn-circle">
                                  <span className="material-symbols-outlined play-icon">play_arrow</span>
                                </div>
                                <div className="fsp-play-badge">
                                  <span className="pulse-red-dot"></span>
                                  <span>Watch Video</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="fsp-sub-img-caption-bar">
                          <div className="fsp-sub-founders-row">
                            <div className="fsp-founder-tag">
                              <span className="pulse-emerald"></span>
                              <strong>{card.founderName}</strong>
                              <span className="role-badge">{card.founderRole}</span>
                            </div>
                            <div className="fsp-founder-tag">
                              <span className="material-symbols-outlined" style={{ fontSize: '15px', color: '#feef00' }}>verified</span>
                              <strong>{card.cofounderName}</strong>
                              <span className="role-badge">{card.cofounderRole}</span>
                            </div>
                          </div>
                          <span className="fsp-sub-img-title">{card.caption}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carousel Controls: Arrows & Slide Pills */}
            {flagshipCardsData.length > 1 && (
              <div
                className="fsp-flagship-carousel-controls"
                onMouseEnter={() => setIsHoveringFlagship(true)}
                onMouseLeave={() => setIsHoveringFlagship(false)}
              >
                <button
                  type="button"
                  onClick={handlePrevFlagship}
                  className="fsp-carousel-arrow-btn"
                  aria-label="Previous Flagship Project"
                >
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>

                <div className="fsp-carousel-dots">
                  {flagshipCardsData.map((card, idx) => (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => {
                        setIsPlayingFlagshipVideo(false);
                        setFlagshipIndex(idx);
                      }}
                      className={`fsp-carousel-dot ${flagshipIndex === idx ? 'active' : ''}`}
                    >
                      <span>{card.title}</span>
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleNextFlagship}
                  className="fsp-carousel-arrow-btn"
                  aria-label="Next Flagship Project"
                >
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Aptitude Preparation & Elimination Rounds Mastery Section (White Theme) */}
      <section className="fsp-aptitude-section" id="aptitude">
        <div className="fsp-container">
          <div className="fsp-aptitude-content-grid">

            {/* Left Column: Interactive macOS Styled Dashboard Portal Frame */}
            <div className="fsp-apt-dashboard-frame">
              {/* Window Header */}
              <div className="fsp-apt-header">
                <div className="fsp-window-dots">
                  <span className="dot red"></span>
                  <span className="dot yellow"></span>
                  <span className="dot green"></span>
                </div>
                <div className="fsp-apt-title">
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', color: '#60a5fa' }}>terminal</span>
                  <span>APTITUDE LEARNING PORTAL</span>
                </div>
                <div className="fsp-apt-status-live">
                  <span className="fsp-live-pulse-dot"></span>
                  <span>HD ACCESS</span>
                </div>
              </div>

              {/* Video Mock Player */}
              <div className="fsp-mock-player-container">
                <div className="fsp-player-inner">
                  {/* Subtle Grid overlay */}
                  <div className="fsp-grid-overlay"></div>

                  {/* Player Glowing Aura */}
                  <div className="fsp-player-glow"></div>

                  <div className="fsp-player-center-content">
                    <span className="fsp-video-content-pill">
                      <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>play_circle</span>
                      VIDEO CONTENT
                    </span>
                    <div className="fsp-lock-ring-container">
                      <div className="fsp-lock-ripple"></div>
                      <span className="material-symbols-outlined fsp-lock-icon">lock</span>
                    </div>
                    <h4 className="fsp-player-topic-heading">{activeAptTopic.name}</h4>
                    <p className="fsp-player-sub-hint">Full HD Access provided upon enrollment</p>
                  </div>

                  {/* Player Controls Bar */}
                  <div className="fsp-player-bottom-bar">
                    <div className="fsp-play-circle-btn">
                      <span className="material-symbols-outlined">play_arrow</span>
                    </div>
                    <div className="fsp-player-timeline">
                      <div className="fsp-timeline-track">
                        <div className="fsp-timeline-fill" style={{ width: '42%' }}></div>
                        <div className="fsp-timeline-knob" style={{ left: '42%' }}></div>
                      </div>
                    </div>
                    <span className="fsp-duration-tag">{activeAptTopic.duration || 'Full HD'}</span>
                  </div>
                </div>
              </div>

              {/* Interactive Topics List Inside Dashboard */}
              <div className="fsp-apt-topics-box">
                <div className="fsp-apt-list-header">
                  <div className="fsp-apt-list-title">
                    <span>Selected Lectures ({currentAptCategory.topics.length})</span>
                  </div>
                  <span className="fsp-apt-badge-highlight">300+ QUESTIONS WITH VIDEO EXPLANATIONS</span>
                </div>

                <div className="fsp-apt-lectures-scroll">
                  {currentAptCategory.topics.map((topic, index) => {
                    const isActive = aptitudeActiveTopicIndex === index;
                    return (
                      <div
                        key={topic.name}
                        className={`fsp-lecture-item ${isActive ? 'active' : ''}`}
                        onClick={() => setAptitudeActiveTopicIndex(index)}
                      >
                        <div className="fsp-lecture-status-icon">
                          {isActive ? (
                            <div className="fsp-equalizer-wave">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          ) : (
                            <span className="material-symbols-outlined fsp-play-idle-icon">play_arrow</span>
                          )}
                        </div>
                        <div className="fsp-lecture-text-wrap">
                          <span className="fsp-lecture-name">{topic.name}</span>
                          <span className="fsp-lecture-meta">{topic.questions} • {topic.duration}</span>
                        </div>
                        <span className="material-symbols-outlined fsp-lecture-lock">lock</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column: Text Information, Category Tabs, Stats & Checklist */}
            <div className="fsp-aptitude-info-pane">
              <div className="fsp-apt-badge">
                <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>psychology</span>
                APTITUDE PREPARATION
              </div>

              <h2 className="fsp-apt-main-title">
                Crack Elimination Rounds: <br />
                <span className="fsp-text-gradient">300+ Aptitude Questions</span>
              </h2>

              <p className="fsp-apt-main-desc">
                Breeze through the initial hiring assessments of major IT giants. We cover <strong>34 critical topics</strong> across Quantitative, Logical Reasoning, and Verbal sections with premium <strong>video access</strong> and step-by-step explanations.
              </p>

              {/* Interactive Category Switcher Tabs */}
              <div className="fsp-apt-category-tabs">
                {Object.keys(aptitudeCategories).map((key) => {
                  const cat = aptitudeCategories[key];
                  const isActive = aptitudeCategory === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`fsp-apt-tab-btn ${isActive ? 'active' : ''}`}
                      onClick={() => handleAptCategoryChange(key)}
                    >
                      <span className="material-symbols-outlined fsp-apt-tab-icon">{cat.icon}</span>
                      <span className="fsp-apt-tab-name">{cat.title}</span>
                      <span className="fsp-apt-tab-pill">{cat.count}</span>
                    </button>
                  );
                })}
              </div>

              {/* 3 Quick Stats Grid */}
              <div className="fsp-apt-stats-grid">
                <div className="fsp-apt-stat-card">
                  <strong className="fsp-stat-number">34+</strong>
                  <span className="fsp-stat-label">CORE TOPICS</span>
                </div>
                <div className="fsp-apt-stat-card">
                  <strong className="fsp-stat-number">300+</strong>
                  <span className="fsp-stat-label">PRACTICE QUESTIONS</span>
                </div>
                <div className="fsp-apt-stat-card">
                  <strong className="fsp-stat-number">100%</strong>
                  <span className="fsp-stat-label">VIDEO SOLUTIONS</span>
                </div>
              </div>

              {/* Feature Checklist */}
              <div className="fsp-apt-checklist">
                <div className="fsp-apt-check-item">
                  <div className="fsp-check-circle">
                    <span className="material-symbols-outlined">check</span>
                  </div>
                  <div>
                    <h4>Complete Video Explanations</h4>
                    <p>Every question is solved step-by-step to build speed, accuracy, and quick pattern recognition.</p>
                  </div>
                </div>

                <div className="fsp-apt-check-item">
                  <div className="fsp-check-circle">
                    <span className="material-symbols-outlined">check</span>
                  </div>
                  <div>
                    <h4>Company-Wise Shortcuts & Tricks</h4>
                    <p>Special sessions focused on time-saving techniques tested by TCS, Infosys, Cognizant, Wipro & Product companies.</p>
                  </div>
                </div>

                <div className="fsp-apt-check-item">
                  <div className="fsp-check-circle">
                    <span className="material-symbols-outlined">check</span>
                  </div>
                  <div>
                    <h4>Topic-Wise Formulas & Practice Sets</h4>
                    <p>Structured practice assignments and formula cheat sheets to reinforce every core concept before tests.</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 20+ Mentors & Team Leads Ecosystem Section */}
      <section className="fsp-mentorship-section" id="mentorship">
        <div className="fsp-container">
          {/* Section Header */}
          <div className="fsp-section-header">
            <div className="fsp-hero-badge" style={{ margin: '0 auto 16px auto', background: 'rgba(0, 40, 142, 0.08)', color: '#00288e', border: '1px solid rgba(0, 40, 142, 0.2)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>groups</span>
              20+ MENTORS & TEAM LEADS
            </div>
            <h2 className="fsp-section-title">
              Personalized Mentorship by <span className="fsp-text-gradient">20+ Mentors & Team Leads</span>
            </h2>
            <p className="fsp-section-subtitle">
              Learn directly from experienced industry software leads standing by to guide your journey from day 1 to placement.
            </p>
          </div>

          {/* Interactive Mentorship Stats Header Row */}
          <div className="fsp-mentor-stats-grid">
            <div className="fsp-mentor-stat-card">
              <div className="fsp-mentor-stat-icon-box" style={{ background: 'rgba(0, 40, 142, 0.1)', color: '#00288e' }}>
                <span className="material-symbols-outlined">supervisor_account</span>
              </div>
              <div className="fsp-mentor-stat-number">20+</div>
              <div className="fsp-mentor-stat-label">Mentors & Team Leads</div>
              <div className="fsp-mentor-stat-sub">Active daily guidance</div>
            </div>

            <div className="fsp-mentor-stat-card">
              <div className="fsp-mentor-stat-icon-box" style={{ background: 'rgba(113, 42, 226, 0.1)', color: '#712ae2' }}>
                <span className="material-symbols-outlined">bolt</span>
              </div>
              <div className="fsp-mentor-stat-number">1-on-1</div>
              <div className="fsp-mentor-stat-label">Code & Doubt Sessions</div>
              <div className="fsp-mentor-stat-sub">Code Review</div>
            </div>

            <div className="fsp-mentor-stat-card">
              <div className="fsp-mentor-stat-icon-box" style={{ background: 'rgba(76, 132, 39, 0.1)', color: '#4c8427' }}>
                <span className="material-symbols-outlined">rate_review</span>
              </div>
              <div className="fsp-mentor-stat-number">100%</div>
              <div className="fsp-mentor-stat-label">Line-by-Line Code Reviews</div>
              <div className="fsp-mentor-stat-sub">Clean code practices</div>
            </div>

            <div className="fsp-mentor-stat-card">
              <div className="fsp-mentor-stat-icon-box" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#d97706' }}>
                <span className="material-symbols-outlined">trending_up</span>
              </div>
              <div className="fsp-mentor-stat-number">Continuous</div>
              <div className="fsp-mentor-stat-label">Progress Tracking</div>
              <div className="fsp-mentor-stat-sub">Regular feedback rounds</div>
            </div>
          </div>

          {/* Master Educator Showcase Card: Shashi Sir */}
          <div className="fsp-master-sir-card">
            <div className="fsp-master-sir-left">
              <div className="fsp-master-sir-badge">
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>stars</span>
                TEACHER
              </div>
              <h3 className="fsp-master-sir-title">
                Learn Directly from <span className="fsp-text-gradient">Shashi Sir</span>
              </h3>
              <p className="fsp-master-sir-role">
                Founder and Teacher — Core2web
                <span className="fsp-sir-exp-highlight">⭐ 10+ Years Experience</span>
              </p>
            </div>

            <div className="fsp-master-sir-right">
              <div className="fsp-master-sir-img-frame">
                <img
                  src="/assets/images/landing/ShashiSir-min.png"
                  alt="Shashi Sir - Founder & Teacher at Core2web"
                  className="fsp-master-sir-img"
                />
                <div className="fsp-master-sir-experience-tag">
                  <span className="number">10+ Years</span>
                  <span className="text">Teaching Excellence</span>
                </div>
              </div>
            </div>
          </div>

          {/* 10 Key Mentorship Highlights Grid */}
          <div className="fsp-mentorship-grid-10">
            {mentorshipHighlights.map((item, idx) => (
              <div key={idx} className="fsp-mentorship-card-10">
                <div className="fsp-mentorship-emoji-box" style={{ background: item.bgColor, color: item.color }}>
                  <span style={{ fontSize: '26px' }}>{item.emoji}</span>
                </div>
                <div className="fsp-mentorship-card-content">
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certification Highlight Section */}
      <section className="fsp-cert-section" id="certification">
        <div className="fsp-cert-grid">
          <div>
            <div className="fsp-cert-badge">
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>verified</span>
              OFFICIAL CERTIFICATION
            </div>
            <h2 className="fsp-section-title" style={{ color: '#ffffff', textAlign: 'left', fontSize: '36px', marginBottom: '16px' }}>
              Earn a Recognized <span style={{ color: '#feef00' }}>Industry Certificate</span>
            </h2>
            <p style={{ color: '#c4c5d5', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
              Validate your full-stack expertise with Core2web's official course completion & project endorsement certificate. Shared directly with hiring partners and top IT recruiters.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                { icon: 'workspace_premium', title: 'Verifiable Credentials', desc: 'Unique QR code & digital link to verify your certificate online.' },
                { icon: 'share', title: 'Shareable on LinkedIn & Resume', desc: 'Add directly to your LinkedIn licenses & certifications profile.' },
                { icon: 'code_blocks', title: 'Project Endorsement', desc: 'Highlights your 20+ projects & 3 major full-stack applications built.' },
              ].map((feat, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ background: 'rgba(254, 239, 0, 0.15)', color: '#feef00', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>{feat.icon}</span>
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '17px', fontWeight: '700', color: '#ffffff' }}>{feat.title}</h4>
                    <p style={{ margin: 0, color: '#a0a5ba', fontSize: '14px' }}>{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="fsp-cert-card-box">
            <img
              alt="Core2web Full Stack Certificate Preview"
              className="fsp-cert-img"
              src="/assets/full%20stack/fullstack-certificate.png"
            />
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#feef00', fontWeight: '700', fontSize: '14px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>stars</span>
              Core2web Certified Java Full Stack Developer
            </div>
          </div>
        </div>
      </section>

      {/* Internship & Industrial Training Section with Internship Letter Showcase */}
      <section className="fsp-intern-section" id="internship">
        <div className="fsp-container">
          <div className="fsp-section-header" style={{ marginBottom: '40px' }}>
            <div className="fsp-hero-badge" style={{ margin: '0 auto 16px auto' }}>
              <span>🚀</span> INDUSTRY READINESS & EXPERIENCE
            </div>
            <h2 className="fsp-section-title">Hands-on Industrial Internship & Experience Letter</h2>
            <p className="fsp-section-subtitle">
              Get real software engineering experience, work on production codebases, and earn an official Internship Completion Letter.
            </p>
          </div>

          <div className="fsp-cert-grid" style={{ alignItems: 'center' }}>
            {/* Left Column: 3 Key Industrial Pillars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { icon: 'terminal', title: 'Industry-Level Projects', desc: 'Build real-world projects using industry-standard Java & React technologies.' },
                { icon: 'badge', title: 'Internship Completion Letter', desc: 'Receive an official Internship Experience Letter & Recommendation from Incubators Systems by Core2web.' },

              ].map((card, idx) => (
                <div key={idx} className="fsp-intern-card" style={{ boxSizing: 'border-box' }}>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div className="fsp-intern-icon-box" style={{ marginBottom: 0, flexShrink: 0 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>{card.icon}</span>
                    </div>
                    <div>
                      <h3 style={{ fontSize: '17px', fontWeight: '800', margin: '0 0 4px 0', color: '#111c2d' }}>{card.title}</h3>
                      <p style={{ fontSize: '13px', color: '#444653', margin: 0, lineHeight: '1.5' }}>{card.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column: Internship Letter Card Preview */}
            <div className="fsp-cert-card-box" style={{ background: '#f0f3ff', border: '1px solid rgba(0, 40, 142, 0.15)', padding: '20px' }}>
              <img
                alt="Incubators Systems by Core2web Internship Completion Letter"
                className="fsp-cert-img"
                style={{ maxHeight: '360px', boxShadow: '0 12px 30px rgba(0, 40, 142, 0.15)' }}
                src="/assets/images/upcomingCourse/internship_letter.png"
              />
              <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#00288e', fontWeight: '800', fontSize: '14px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#4c8427' }}>verified</span>
                Official Internship Completion Letter (Incubators Systems)
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Batch Schedule & Timings Section */}
      <section className="fsp-schedule-section" id="schedule">
        <div className="fsp-container">
          <div className="fsp-section-header">
            <div className="fsp-hero-badge" style={{ margin: '0 auto 16px auto', background: 'rgba(113, 42, 226, 0.08)', color: '#712ae2', border: '1px solid rgba(113, 42, 226, 0.2)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>schedule</span>
              BATCH TIMINGS & DAILY SCHEDULE
            </div>
            <h2 className="fsp-section-title">Class Schedule & Practical Timings</h2>
            <p className="fsp-section-subtitle">
              Structured daily routine designed for maximum hands-on coding, live lectures, and concept mastery.
            </p>
          </div>

          {/* Schedule Cards Grid */}
          <div className="fsp-schedule-grid">
            {/* Theory Lectures Card */}
            <div className="fsp-schedule-card lecture-card">
              <div className="fsp-schedule-card-header">
                <div className="fsp-schedule-icon-box lecture-icon">
                  <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>school</span>
                </div>
                <div>
                  <span className="fsp-schedule-tag lecture-tag">6 DAYS A WEEK</span>
                  <h3 className="fsp-schedule-card-title">Theory & Concept Lectures</h3>
                </div>
              </div>

              <div className="fsp-schedule-details">
                <div className="fsp-schedule-info-row">
                  <span className="material-symbols-outlined icon">calendar_today</span>
                  <span className="label">Days:</span>
                  <span className="value">Mon - Sat</span>
                </div>
                <div className="fsp-schedule-info-row highlight-row lecture-time-bg">
                  <span className="material-symbols-outlined icon">alarm</span>
                  <span className="label">Time:</span>
                  <span className="value time-value">5:00 PM - 8:00 PM</span>
                </div>
              </div>

              <div className="fsp-schedule-features">
                <div className="fsp-schedule-feature-item">
                  <span className="material-symbols-outlined check">check_circle</span>
                  <span>Interactive deep-dive theory lectures</span>
                </div>
                <div className="fsp-schedule-feature-item">
                  <span className="material-symbols-outlined check">check_circle</span>
                  <span>In-depth architecture & live code walkthroughs</span>
                </div>
              </div>
            </div>

            {/* Practical Sessions Card */}
            <div className="fsp-schedule-card practical-card">
              <div className="fsp-schedule-card-header">
                <div className="fsp-schedule-icon-box practical-icon">
                  <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>terminal</span>
                </div>
                <div>
                  <span className="fsp-schedule-tag practical-tag">6 DAYS A WEEK</span>
                  <h3 className="fsp-schedule-card-title">Practicals Session</h3>
                </div>
              </div>

              <div className="fsp-schedule-details">
                <div className="fsp-schedule-info-row">
                  <span className="material-symbols-outlined icon">calendar_today</span>
                  <span className="label">Days:</span>
                  <span className="value">Mon - Sat</span>
                </div>
                <div className="fsp-schedule-info-row highlight-row">
                  <span className="material-symbols-outlined icon">alarm</span>
                  <span className="label">Time:</span>
                  <span className="value time-value">3:00 PM - 4:30 PM</span>
                </div>
              </div>

              <div className="fsp-schedule-features">
                <div className="fsp-schedule-feature-item">
                  <span className="material-symbols-outlined check">check_circle</span>
                  <span>Daily hands-on practical coding lab</span>
                </div>
                <div className="fsp-schedule-feature-item">
                  <span className="material-symbols-outlined check">check_circle</span>
                  <span>Live doubt solving & mentor guidance</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Summary Pill Bar */}
          <div className="fsp-schedule-summary-bar">
            <span className="material-symbols-outlined" style={{ color: '#712ae2' }}>bolt</span>
            <span>Summary: <strong>6 Days Lectures (Mon - Sat)</strong> & <strong>6 Days Practicals (Mon - Sat)</strong></span>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="fsp-pricing-section" id="pricing">
        <div className="fsp-pricing-card">
          <div className="fsp-pricing-badge">Limited Seats</div>
          <h2 style={{ fontSize: '32px', fontWeight: '800', margin: '16px 0 4px 0' }}>Complete Course</h2>
          <p style={{ color: '#444653', margin: 0 }}>Everything you need to become a Java Full Stack Developer</p>
          <div className="fsp-pricing-price">
            ₹17,000 <span>+ 18% GST</span>
          </div>
          <ul className="fsp-pricing-list">
            {[
              'Complete Frontend & Backend Training',
              '20+ Projects + 1 Super-X Project',
              'Doubt Solving Sessions',
              'Interview Preparation',
              'Course Completion Certificate',
              'Internship Completion Letter',
              'Placement Assistance'
            ].map((item, idx) => (
              <li key={idx}>
                <span className="material-symbols-outlined icon">check_circle</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <button onClick={() => openEnrollModal('admission')} className="fsp-btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: '18px', padding: '16px' }}>
            Enroll Now
          </button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="fsp-cta-section">
        <div className="fsp-container">
          <h2 className="fsp-cta-title">Ready to start your Full Stack journey?</h2>
          <p className="fsp-cta-desc">Join thousands of students who have transformed their careers with Core2web.</p>
          <button onClick={() => openEnrollModal('admission')} className="fsp-cta-btn">
            Join Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="fsp-footer">
        <div className="fsp-footer-container">
          <div className="fsp-footer-top-grid">
            {/* Column 1: Brand & Tagline & Socials */}
            <div className="fsp-footer-col brand-col">
              <a href="/" className="fsp-footer-logo-link" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                <img
                  alt="Core2web Logo"
                  className="fsp-footer-logo-img"
                  src="/assets/images/landing/c2w-logo-full.svg"
                />
              </a>
              <p className="fsp-footer-tagline">
                Empowering students to master high-end enterprise software engineering, real-world development, and placement excellence.
              </p>
              <div className="fsp-footer-socials">
                <a
                  href="https://www.youtube.com/c/Core2WebTechnologies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fsp-social-btn"
                  title="YouTube"
                >
                  <img src="/assets/images/footer/youtubelogo.svg" alt="YouTube" />
                </a>
                <a
                  href="https://www.instagram.com/core2web"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fsp-social-btn"
                  title="Instagram"
                >
                  <img src="/assets/images/footer/insta-logo.svg" alt="Instagram" />
                </a>
                <a
                  href="https://www.linkedin.com/company/core2web-technologies/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fsp-social-btn"
                  title="LinkedIn"
                >
                  <img src="/assets/images/footer/linkedinlogo.svg" alt="LinkedIn" />
                </a>
                <a
                  href="https://wa.me/+919665244353"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fsp-social-btn"
                  title="WhatsApp"
                >
                  <img src="/assets/images/footer/whatsappLogo.svg" alt="WhatsApp" />
                </a>
              </div>
            </div>

            {/* Column 2: Navigation */}
            <div className="fsp-footer-col">
              <h4 className="fsp-footer-heading">Quick Navigation</h4>
              <ul className="fsp-footer-nav-list">
                <li><button type="button" onClick={() => scrollToSection('overview')}>Overview</button></li>
                <li><button type="button" onClick={() => scrollToSection('roadmap')}>Full Stack Formula</button></li>
                <li><button type="button" onClick={() => scrollToSection('projects')}>Projects (20+)</button></li>
                <li><button type="button" onClick={() => scrollToSection('curriculum')}>Curriculum & Syllabus</button></li>
                <li><button type="button" onClick={() => scrollToSection('aptitude')}>Aptitude Preparation (300+)</button></li>
                <li><button type="button" onClick={() => scrollToSection('mentorship')}>Mentorship</button></li>
                <li><button type="button" onClick={() => scrollToSection('schedule')}>Class Schedule</button></li>
                <li><button type="button" onClick={() => scrollToSection('pricing')}>Course Pricing</button></li>
              </ul>
            </div>

            {/* Column 3: Contact & Support */}
            <div className="fsp-footer-col">
              <h4 className="fsp-footer-heading">Contact & Admissions</h4>
              <div className="fsp-footer-contact-items">
                <div className="fsp-contact-item">
                  <span className="material-symbols-outlined icon">call</span>
                  <div>
                    <span className="label">Admissions Helpline</span>
                    <a href="tel:+919665244353">+91 9665244353</a> / <a href="tel:+919730435300">9730435300</a>
                  </div>
                </div>
                <div className="fsp-contact-item">
                  <span className="material-symbols-outlined icon">mail</span>
                  <div>
                    <span className="label">Official Email</span>
                    <a href="mailto:core2webinfo@gmail.com">core2webinfo@gmail.com</a>
                  </div>
                </div>
                <div className="fsp-contact-item">
                  <span className="material-symbols-outlined icon">location_on</span>
                  <div>
                    <span className="label">Campus Location</span>
                    <span>Core2web, 3rd Floor, Walhekar Properties, above HDFC Bank, Narhe, Pune, Maharashtra 411041</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Legal & Copyright Bar */}
          <div className="fsp-footer-bottom">
            <p className="fsp-footer-copy">
              © {new Date().getFullYear()} Core2web®. All rights reserved.
            </p>
            <div className="fsp-footer-legal">
              <a href="/refund" onClick={(e) => { e.preventDefault(); navigate('/refund'); }}>Privacy Policy</a>
              <span className="fsp-legal-sep">•</span>
              <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>Terms of Service</a>
              <span className="fsp-legal-sep">•</span>
              <a href="/refund" onClick={(e) => { e.preventDefault(); navigate('/refund'); }}>Refund Policy</a>
              <span className="fsp-legal-sep">•</span>
              <a href="/enquiry-form" onClick={(e) => { e.preventDefault(); navigate('/enquiry-form'); }}>Contact Support</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Enrollment Modal Dialog */}
      {isEnrollModalOpen && (
        <div className="fsp-modal-overlay" onClick={closeEnrollModal}>
          <div className="fsp-modal-container" onClick={(e) => e.stopPropagation()}>
            {/* Modal Close Button */}
            <button className="fsp-modal-close" onClick={closeEnrollModal} title="Close Modal">
              <span className="material-symbols-outlined">close</span>
            </button>

            {!isSubmitted ? (
              <>
                {/* Modal Header */}
                <div className="fsp-modal-header">
                  <div className="fsp-modal-badge">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>edit_document</span>
                    <span>OFFICIAL ENROLLMENT</span>
                  </div>
                  <h3 className="fsp-modal-title">Java Full Stack Development Course</h3>
                  <p className="fsp-modal-subtitle">
                    Reserve your seat for Core2web's premium Full Stack program.
                  </p>
                </div>

                {/* Enrollment Form */}
                <form onSubmit={handleEnrollSubmit} className="fsp-enroll-form">
                  {/* Field: Seat Type Toggle */}
                  <div className="fsp-form-group full-width">
                    <label className="fsp-form-label">
                      Seat Type Selection <span className="req">*</span>
                    </label>

                    <div className="fsp-seat-toggle-container">
                      <button
                        type="button"
                        className={`fsp-seat-toggle-btn ${enrollForm.seatType === 'admission' ? 'active-admission' : ''}`}
                        onClick={() => handleEnrollChange('seatType', 'admission')}
                      >
                        <span className="toggle-main">1. Confirm seat for admission</span>
                      </button>

                      <button
                        type="button"
                        className={`fsp-seat-toggle-btn ${enrollForm.seatType === 'demo' ? 'active-demo' : ''}`}
                        onClick={() => handleEnrollChange('seatType', 'demo')}
                      >
                        <span className="toggle-main">2. Confirm seat for demo lecture</span>
                      </button>
                    </div>
                  </div>

                  {/* Field 1: Full Name */}
                  <div className="fsp-form-group">
                    <label className="fsp-form-label">
                      Full Name <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={enrollForm.name}
                      onChange={(e) => handleEnrollChange('name', e.target.value)}
                      className="fsp-form-input"
                    />
                  </div>

                  {/* Field 2: Contact Number */}
                  <div className="fsp-form-group">
                    <label className="fsp-form-label">
                      Contact Number <span className="req">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      pattern="[0-9]{10}"
                      placeholder="10-digit mobile number"
                      value={enrollForm.contact}
                      onChange={(e) => handleEnrollChange('contact', e.target.value)}
                      className="fsp-form-input"
                    />
                  </div>

                  {/* Field 3: College Name */}
                  <div className="fsp-form-group">
                    <label className="fsp-form-label">
                      College Name <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. COEP Pune / MIT World Peace"
                      value={enrollForm.college}
                      onChange={(e) => handleEnrollChange('college', e.target.value)}
                      className="fsp-form-input"
                    />
                  </div>

                  {/* Field 4: Year of Study (FE, SE, TE, BE, Other) */}
                  <div className="fsp-form-group">
                    <label className="fsp-form-label">
                      Year of Study <span className="req">*</span>
                    </label>
                    <select
                      value={enrollForm.year}
                      onChange={(e) => handleEnrollChange('year', e.target.value)}
                      className="fsp-form-select"
                    >
                      <option value="FE">FE</option>
                      <option value="SE">SE</option>
                      <option value="TE">TE</option>
                      <option value="BE">BE</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Field 5: Gender */}
                  <div className="fsp-form-group">
                    <label className="fsp-form-label">
                      Gender <span className="req">*</span>
                    </label>
                    <div className="fsp-gender-pills">
                      {['Male', 'Female', 'Other'].map((g) => (
                        <button
                          key={g}
                          type="button"
                          className={`fsp-gender-pill ${enrollForm.gender === g ? 'selected' : ''}`}
                          onClick={() => handleEnrollChange('gender', g)}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Field 6: Reference (ref) */}
                  <div className="fsp-form-group">
                    <label className="fsp-form-label">
                      Reference (Referred By / Source)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Friend Name, Instagram, YouTube, C2W Student"
                      value={enrollForm.reference}
                      onChange={(e) => handleEnrollChange('reference', e.target.value)}
                      className="fsp-form-input"
                    />
                  </div>

                  {/* Form Submit Button */}
                  <div className="fsp-form-group full-width" style={{ marginTop: '8px' }}>
                    <button type="submit" className="fsp-enroll-submit-btn" disabled={isSubmitting}>
                      <span>
                        {isSubmitting
                          ? 'Submitting...'
                          : enrollForm.seatType === 'admission'
                            ? 'Confirm Seat For Admission'
                            : 'Confirm Seat For Demo Lecture'}
                      </span>
                      <span className="material-symbols-outlined">
                        {isSubmitting ? 'hourglass_empty' : 'arrow_forward'}
                      </span>
                    </button>
                  </div>
                </form>
              </>
            ) : (
              /* Submission Success Feedback Card */
              <div className="fsp-enroll-success-box">
                <div className="success-icon-wrap">
                  <span className="material-symbols-outlined">check_circle</span>
                </div>
                <h3>Seat Registration Confirmed!</h3>
                <p className="success-desc">
                  Thank you, <strong>{enrollForm.name}</strong>! Your request to{' '}
                  <strong>{enrollForm.seatType === 'admission' ? 'Confirm seat for admission' : 'Confirm seat for demo lecture'}</strong>{' '}
                  has been successfully submitted to Core2web.
                </p>

                <div className="success-details-card">
                  <div className="detail-row">
                    <span className="lbl">Contact:</span>
                    <span className="val">{enrollForm.contact}</span>
                  </div>
                  <div className="detail-row">
                    <span className="lbl">College:</span>
                    <span className="val">{enrollForm.college} ({enrollForm.year})</span>
                  </div>
                  <div className="detail-row">
                    <span className="lbl">Seat Option:</span>
                    <span className="val-highlight">
                      {enrollForm.seatType === 'admission' ? 'Seat Reserved for Admission' : 'Pass Reserved for Demo Lecture'}
                    </span>
                  </div>
                </div>

                <button onClick={closeEnrollModal} className="fsp-enroll-done-btn">
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ==========================================================================
         PROJECT DETAILS & TOPICS COVERED MODAL POPUP
         ========================================================================== */}
      {selectedProjectModal && (
        <div className="fsp-modal-overlay" onClick={closeProjectModal}>
          <div className="fsp-project-modal-card" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="fsp-project-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span className={`fsp-modal-cat-tag ${selectedProjectModal.category}`}>
                  {selectedProjectModal.category === 'major'
                    ? 'Full Stack Major Project'
                    : selectedProjectModal.category === 'frontend'
                      ? 'Frontend Web Application'
                      : 'Backend'}
                </span>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {selectedProjectModal.tags.map((t, i) => (
                    <span key={i} className="fsp-modal-tech-tag">{t}</span>
                  ))}
                </div>
              </div>
              <button onClick={closeProjectModal} className="fsp-modal-close-btn" aria-label="Close modal">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="fsp-project-modal-body">
              {/* Project Title */}
              <h2 className="fsp-project-modal-title">{selectedProjectModal.title}</h2>
              <p className="fsp-project-modal-desc">{selectedProjectModal.desc}</p>

              {/* UI Preview Section */}
              <div className="fsp-project-ui-container">
                <div className="fsp-browser-bar">
                  <div className="fsp-browser-dots">
                    <span className="dot red"></span>
                    <span className="dot yellow"></span>
                    <span className="dot green"></span>
                  </div>
                  <div className="fsp-browser-url">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', color: '#10b981' }}>lock</span>
                    https://c2w-projects.com/shop-co/
                    {selectedProjectModal.images
                      ? selectedProjectModal.images[activeModalImageIndex]?.label.toLowerCase().replace(/[^a-z0-9]/g, '-')
                      : selectedProjectModal.title.toLowerCase().replace(/[^a-z0-9]/g, '-')}
                  </div>
                </div>

                {/* Multiple UI Screen Switcher Tabs */}
                {selectedProjectModal.images && selectedProjectModal.images.length > 0 && (
                  <div className="fsp-modal-screen-tabs">
                    {selectedProjectModal.images.slice(0, 2).map((screen, sIdx) => {
                      const icons = ['home', 'filter_alt', 'inventory_2', 'shopping_cart', 'dashboard', 'analytics'];
                      const icon = icons[sIdx % icons.length];
                      return (
                        <button
                          key={sIdx}
                          onClick={() => setActiveModalImageIndex(sIdx)}
                          className={`fsp-screen-tab-btn ${activeModalImageIndex === sIdx ? 'active' : ''}`}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                            {icon}
                          </span>
                          {screen.label}
                        </button>
                      );
                    })}

                    {selectedProjectModal.images.length > 2 && (
                      <div
                        className="fsp-screen-tab-btn fsp-more-pill"
                        style={{ cursor: 'default', pointerEvents: 'none', userSelect: 'none' }}
                      >
                        +More
                      </div>
                    )}
                  </div>
                )}

                <div className="fsp-ui-content-view">
                  {selectedProjectModal.images ? (
                    <img
                      src={selectedProjectModal.images[activeModalImageIndex]?.url || selectedProjectModal.image}
                      alt={`${selectedProjectModal.title} UI Preview`}
                      className="fsp-modal-ui-img"
                    />
                  ) : selectedProjectModal.image ? (
                    <img
                      src={selectedProjectModal.image}
                      alt={`${selectedProjectModal.title} UI Preview`}
                      className="fsp-modal-ui-img"
                    />
                  ) : (
                    <div className="fsp-modal-backend-ui">
                      <div className="fsp-backend-terminal-header">
                        <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#60a5fa' }}>terminal</span>
                        <span>REST API Specification & Data Flow</span>
                        <span className="fsp-status-badge">200 OK</span>
                      </div>
                      <div className="fsp-backend-code-box">
                        <div className="code-line"><span className="kw">GET</span> <span className="str">/api/v1/{selectedProjectModal.title.toLowerCase().split(' ')[0]}/data</span></div>
                        <div className="code-line"><span className="kw">Host:</span> api.core2web.in</div>
                        <div className="code-line"><span className="kw">Authorization:</span> Bearer eyJhbGciOiJIUzI1Ni...</div>
                        <div className="code-line comment">// Topics & Architecture Covered:</div>
                        <div className="code-line"><span className="json-key">&#123;</span></div>
                        <div className="code-line indent"><span className="json-prop">"architecture"</span>: <span className="str">"Spring Boot 3.x + Spring Data JPA"</span>,</div>
                        <div className="code-line indent"><span className="json-prop">"database"</span>: <span className="str">"MySQL Relational Schema"</span>,</div>
                        <div className="code-line indent"><span className="json-prop">"status"</span>: <span className="str">"Production Ready API"</span></div>
                        <div className="code-line"><span className="json-key">&#125;</span></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Grid: Topics Covered & Key Features */}
              <div className="fsp-project-modal-grid">
                {/* Left: Topics Covered */}
                <div className="fsp-modal-box">
                  <h3 className="fsp-modal-box-title">
                    <span className="material-symbols-outlined" style={{ color: '#0083a0' }}>auto_stories</span>
                    Topics & Concepts Covered
                  </h3>
                  <div className="fsp-topics-list">
                    {selectedProjectModal.topics?.map((topic, tIdx) => (
                      <div key={tIdx} className="fsp-topic-item">
                        <span className="material-symbols-outlined fsp-topic-check">check_circle</span>
                        <span>{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Key Features Built */}
                <div className="fsp-modal-box">
                  <h3 className="fsp-modal-box-title">
                    <span className="material-symbols-outlined" style={{ color: '#712ae2' }}>verified</span>
                    Key Features Built
                  </h3>
                  <div className="fsp-features-list">
                    {selectedProjectModal.features?.map((feat, fIdx) => (
                      <div key={fIdx} className="fsp-feature-item">
                        <span className="material-symbols-outlined fsp-feature-star">stars</span>
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="fsp-project-modal-footer">
              <button onClick={closeProjectModal} className="fsp-modal-cancel-btn">
                Close Preview
              </button>
              <button
                onClick={() => {
                  closeProjectModal();
                  openEnrollModal('admission');
                }}
                className="fsp-modal-enroll-btn"
              >
                <span>Enroll to Build This Project</span>
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>rocket_launch</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Syllabus Enquiry & Download Popup Modal */}
      {isSyllabusModalOpen && (
        <div className="fsp-syll-modal-overlay" onClick={closeSyllabusModal}>
          <div className="fsp-syll-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="fsp-syll-modal-close" onClick={closeSyllabusModal} type="button">
              <span className="material-symbols-outlined">close</span>
            </button>

            {!isSyllabusSubmitted ? (
              <>
                <div className="fsp-syll-modal-header">
                  <div className="fsp-syll-modal-badge">
                    <span>📄</span> SYLLABUS DOWNLOAD & ENQUIRY
                  </div>
                  <h3 className="fsp-syll-modal-title">Download Full Stack Syllabus</h3>
                  <p className="fsp-syll-modal-subtitle">
                    Enter your details below to submit your enquiry & receive the complete course curriculum PDF instantly.
                  </p>
                </div>

                <form onSubmit={handleSyllabusSubmit} className="fsp-syll-modal-form">
                  <div className="fsp-syll-form-group">
                    <input
                      type="text"
                      placeholder="Name"
                      value={syllabusForm.userName}
                      onChange={(e) => handleSyllabusChange('userName', e.target.value)}
                      required
                    />
                  </div>

                  <div className="fsp-syll-form-group">
                    <input
                      type="tel"
                      placeholder="Contact number"
                      value={syllabusForm.userNumber}
                      onChange={(e) => handleSyllabusChange('userNumber', e.target.value)}
                      required
                      maxLength={10}
                    />
                  </div>

                  <div className="fsp-syll-form-group">
                    <input
                      type="text"
                      placeholder="Reference (Ref)"
                      value={syllabusForm.reference}
                      onChange={(e) => handleSyllabusChange('reference', e.target.value)}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSyllabusSubmitting}
                    className="fsp-syll-submit-btn"
                  >
                    {isSyllabusSubmitting ? (
                      <>
                        <span className="material-symbols-outlined spin">progress_activity</span>
                        Submitting & Downloading...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined">download</span>
                        Submit & Download Syllabus PDF 🚀
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="fsp-syll-success-box">
                <div className="fsp-syll-success-icon">
                  <span className="material-symbols-outlined">check_circle</span>
                </div>
                <h3>Enquiry Submitted Successfully!</h3>
                <p>
                  Thank you! Your details have been saved to our Enquiry Sheet, and your <strong>Full Stack Syllabus PDF</strong> download has started.
                </p>
                <a
                  href="/assets/pdf/full_stack_syllabus.pdf"
                  download="Java_Fullstack_Complete_Syllabus.pdf"
                  onClick={triggerPDFDownload}
                  className="fsp-syll-download-again-btn"
                  style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  <span className="material-symbols-outlined">download</span>
                  Click here to Download PDF Manually
                </a>
                <button
                  type="button"
                  onClick={closeSyllabusModal}
                  className="fsp-btn-secondary"
                  style={{ marginTop: '12px', width: '100%', justifyContent: 'center' }}
                >
                  Done / Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default FullStackCoursePage;
